import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FinancePageLayout, formatMoney } from '@/components/finance';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, RefreshCw, ShieldAlert, Link as LinkIcon, CheckCircle2, Eye, X, Download } from 'lucide-react';

type ReconIssue = {
  id: string;
  status: 'open' | 'in_review' | 'resolved' | 'waived';
  severity: 'low' | 'medium' | 'high' | 'critical';
  source: string;
  issue_type: string;
  amount_expected: number | null;
  amount_found: number | null;
  currency: string | null;
  opened_at: string;
  resolved_at?: string | null;
  resolution_note: string | null;
  owner_user_id?: string | null;
  payment_transaction_id: string | null;
  bank_transaction_id: string | null;
  stripe_event_id?: string | null;
};

type BankTxn = {
  id: string;
  booked_at: string;
  amount: number;
  currency: string;
  direction: 'in' | 'out';
  source: 'stripe' | 'itau' | 'chase' | 'manual';
  description: string | null;
  reference: string | null;
};

type OwnerRow = {
  id: string;
  display_name: string | null;
  first_name: string | null;
  last_name: string | null;
};

type AssigneeOption = {
  id: string;
  name: string;
};

type IssueAuditRow = {
  id: string;
  issue_id: string;
  changed_at: string;
  changed_by_app_user_id: string | null;
  field_name: string;
  old_value: string | null;
  new_value: string | null;
  reason: string | null;
};

type SlaAlertRow = {
  id: string;
  alert_bucket: 'over24' | 'over48';
  status: 'new' | 'acknowledged';
};

type PaymentMeta = {
  id: string;
  payment_id: string | null;
  deal_id: string | null;
  contact_id: string | null;
  transaction_date: string | null;
  amount: number | null;
  currency: string | null;
  payment_method: string | null;
  transaction_type: string | null;
  reference_number: string | null;
  transaction_status: string | null;
  recorded_by: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  deal_name: string | null;
  contact_name: string | null;
  due_date: string | null;
  payment_status: string | null;
  payment_amount: number | null;
  payment_paid_amount: number | null;
  payment_balance: number | null;
  payment_number: string | null;
  payment_description: string | null;
  remaining_allocatable: number | null;
  is_legacy_linked: boolean;
};

type PaymentReconReasonCode =
  | 'partial_stripe'
  | 'timing_gap'
  | 'id_mismatch'
  | 'manual_override'
  | 'cross_client_allocation'
  | 'split_payment'
  | 'other';

type ReceivableCandidate = {
  id: string;
  deal_id: string;
  due_date: string;
  status: string;
  amount: number;
  paid_amount: number;
  balance: number;
  currency: string | null;
  payment_number: string | null;
  description: string | null;
  deal_name: string | null;
  contact_name: string | null;
};

type PaymentReconRow = {
  id: string;
  deal_id: string;
  contact_id: string | null;
  due_date: string;
  status: string;
  amount: number;
  paid_amount: number;
  balance: number;
  currency: string | null;
  payment_number: string | null;
  description: string | null;
  deal_name: string | null;
  contact_name: string | null;
};

type ReconMatchRow = {
  id: string;
  source: 'stripe' | 'itau' | 'chase' | 'manual';
  payment_transaction_id: string | null;
  bank_transaction_id: string | null;
  matched_by: string | null;
  matched_at: string;
  notes: string | null;
};

const STATUS_LABEL: Record<ReconIssue['status'], string> = {
  open: 'Aberta',
  in_review: 'Em análise',
  resolved: 'Resolvida',
  waived: 'Dispensada',
};

const SEVERITY_LABEL: Record<ReconIssue['severity'], string> = {
  critical: 'Crítica',
  high: 'Alta',
  medium: 'Média',
  low: 'Baixa',
};

const SOURCE_LABEL: Record<string, string> = {
  stripe: 'Sistema • Stripe',
  itau: 'Banco • Itaú',
  chase: 'Banco • Chase',
  manual: 'Lançamento manual',
};

const ISSUE_TYPE_LABEL: Record<string, string> = {
  missing: 'Sem extrato',
  mismatch: 'Divergência',
  duplicate: 'Duplicada',
  duplicate_suspected: 'Duplicada suspeita',
  orphan: 'Órfã',
  timing: 'Timing',
};

const PAYMENT_METHOD_LABEL: Record<string, string> = {
  stripe: 'Cartão (Stripe)',
  pix: 'PIX',
  zelle: 'Zelle',
  bank_transfer: 'Transferência bancária',
  cash: 'Dinheiro',
  check: 'Cheque',
  other: 'Outro',
};

const AUDIT_FIELD_LABEL: Record<string, string> = {
  status: 'Status',
  owner_user_id: 'Responsável',
  resolution_note: 'Observação',
  resolved_at: 'Data de resolução',
};

const PAYMENT_RECON_REASON_OPTIONS: Array<{ value: PaymentReconReasonCode; label: string }> = [
  { value: 'partial_stripe', label: 'Pagamento parcial Stripe' },
  { value: 'timing_gap', label: 'Diferença de data (timing)' },
  { value: 'id_mismatch', label: 'ID/Referência divergente' },
  { value: 'manual_override', label: 'Ajuste manual autorizado' },
  { value: 'cross_client_allocation', label: 'Alocação cross-client' },
  { value: 'split_payment', label: 'Rateio entre parcelas' },
  { value: 'other', label: 'Outro' },
];

function getIssueAgeHours(openedAt: string): number {
  const ts = new Date(openedAt).getTime();
  if (!Number.isFinite(ts)) return 0;
  return (Date.now() - ts) / (1000 * 60 * 60);
}

function localTodayIso(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function getEffectiveReceivableStatus(params: {
  status?: string | null;
  dueDate?: string | null;
  amount?: number | null;
  paidAmount?: number | null;
  balance?: number | null;
}): string {
  const status = String(params.status || '').toLowerCase().trim();
  const dueDate = params.dueDate ? String(params.dueDate).slice(0, 10) : null;
  const amount = Number(params.amount || 0);
  const paidAmount = Number(params.paidAmount || 0);
  const balance =
    Number.isFinite(Number(params.balance))
      ? Number(params.balance)
      : Math.max(amount - paidAmount, 0);

  if (status === 'paid' || (amount > 0 && paidAmount >= amount) || balance <= 0) return 'paid';
  if (status === 'cancelled') return 'cancelled';
  if (status === 'partially_paid') return 'partially_paid';
  if (!dueDate) return 'pending';
  return dueDate < localTodayIso() ? 'overdue' : 'pending';
}

function getFriendlyFinanceError(error: any, fallback: string): string {
  const raw = String(error?.message || '');
  if (raw.includes('LOCKED_FINANCE_PERIOD')) {
    return 'Período financeiro travado. Reabra o período em Fechamento para editar.';
  }
  return `${fallback}: ${error?.message || 'erro desconhecido'}`;
}

function inferExpectedDirection(issue: ReconIssue, paymentMeta: PaymentMeta | null): 'in' | 'out' | null {
  const txType = String(paymentMeta?.transaction_type || '').toLowerCase();
  if (
    txType.includes('refund') ||
    txType.includes('chargeback') ||
    txType.includes('reversal') ||
    txType.includes('estorno') ||
    txType.includes('saida') ||
    txType.includes('debit') ||
    txType.includes('withdraw')
  ) {
    return 'out';
  }
  if (
    txType.includes('payment') ||
    txType.includes('charge') ||
    txType.includes('credit') ||
    txType.includes('receipt') ||
    txType.includes('entrada')
  ) {
    return 'in';
  }
  if (typeof issue.amount_expected === 'number') {
    return issue.amount_expected >= 0 ? 'in' : 'out';
  }
  if (typeof issue.amount_found === 'number') {
    return issue.amount_found >= 0 ? 'in' : 'out';
  }
  return null;
}

export default function Reconciliation() {
  const [searchParams] = useSearchParams();
  const PAGE_SIZE = 100;

  const [issues, setIssues] = useState<ReconIssue[]>([]);
  const [paymentReconRows, setPaymentReconRows] = useState<PaymentReconRow[]>([]);
  const [statsIssues, setStatsIssues] = useState<ReconIssue[]>([]);
  const [bankTxOptions, setBankTxOptions] = useState<BankTxn[]>([]);
  const [ownerNames, setOwnerNames] = useState<Record<string, string>>({});
  const [assignees, setAssignees] = useState<AssigneeOption[]>([]);
  const [auditByIssue, setAuditByIssue] = useState<Record<string, IssueAuditRow[]>>({});
  const [paymentMetaByTxId, setPaymentMetaByTxId] = useState<Record<string, PaymentMeta>>({});
  const [bankMetaById, setBankMetaById] = useState<Record<string, BankTxn>>({});
  const [matchByIssueId, setMatchByIssueId] = useState<Record<string, ReconMatchRow>>({});
  const [matchedPaymentTxIds, setMatchedPaymentTxIds] = useState<Set<string>>(new Set());
  const [matchedBankTxIds, setMatchedBankTxIds] = useState<Set<string>>(new Set());
  const [currentAppUserId, setCurrentAppUserId] = useState<string | null>(null);
  const [slaAlertSummary, setSlaAlertSummary] = useState({ totalNew: 0, over24New: 0, over48New: 0 });
  const [loading, setLoading] = useState(false);
  const [totalIssuesCount, setTotalIssuesCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [runningMatch, setRunningMatch] = useState(false);
  const [runningAlerts, setRunningAlerts] = useState(false);
  const [ackingAlerts, setAckingAlerts] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>(
    ['critical', 'high', 'medium', 'low'].includes(searchParams.get('severity') || '') ? (searchParams.get('severity') as any) : 'all'
  );
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'in_review' | 'resolved' | 'waived'>(
    ['all', 'open', 'in_review', 'resolved', 'waived'].includes(searchParams.get('status') || '') ? (searchParams.get('status') as any) : 'open'
  );
  const [sourceFilter, setSourceFilter] = useState<'all' | 'stripe' | 'itau' | 'chase' | 'manual'>(
    ['all', 'stripe', 'itau', 'chase', 'manual'].includes(searchParams.get('source') || '') ? (searchParams.get('source') as any) : 'all'
  );
  const [slaFilter, setSlaFilter] = useState<'all' | 'over24' | 'over48'>(
    ['all', 'over24', 'over48'].includes(searchParams.get('sla') || '') ? (searchParams.get('sla') as any) : 'all'
  );
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'missing' | 'mismatch' | 'duplicate' | 'duplicate_suspected' | 'orphan' | 'timing'>(
    ['all', 'missing', 'mismatch', 'duplicate', 'duplicate_suspected', 'orphan', 'timing'].includes(searchParams.get('type') || '')
      ? (searchParams.get('type') as any)
      : 'all'
  );
  const [paymentMethodFilter, setPaymentMethodFilter] = useState<string>('all');
  const [issueSearchTerm, setIssueSearchTerm] = useState(searchParams.get('q') || '');
  const [issueOpenedFrom, setIssueOpenedFrom] = useState(searchParams.get('from') || '');
  const [issueOpenedTo, setIssueOpenedTo] = useState(searchParams.get('to') || '');
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('');
  const [paymentDueFrom, setPaymentDueFrom] = useState('');
  const [paymentDueTo, setPaymentDueTo] = useState('');
  const [sortBy, setSortBy] = useState<'opened_at' | 'severity' | 'sla' | 'amount'>('sla');
  const [selectedIssueIds, setSelectedIssueIds] = useState<string[]>([]);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [activeIssueId, setActiveIssueId] = useState<string | null>(null);
  const [selectedBankTxnId, setSelectedBankTxnId] = useState<string>('');
  const [manualNote, setManualNote] = useState<string>('');
  const [savingManualMatch, setSavingManualMatch] = useState(false);
  const [matchTotalCount, setMatchTotalCount] = useState(0);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [candidateBankTxns, setCandidateBankTxns] = useState<BankTxn[]>([]);
  const [candidatePaymentTxns, setCandidatePaymentTxns] = useState<PaymentMeta[]>([]);
  const [candidateReceivables, setCandidateReceivables] = useState<ReceivableCandidate[]>([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState<string>('');
  const [selectedPaymentTransactionId, setSelectedPaymentTransactionId] = useState<string>('');
  const [manualBankScope, setManualBankScope] = useState<'compatible' | 'all'>('compatible');
  const [paymentReconSheetOpen, setPaymentReconSheetOpen] = useState(false);
  const [activePaymentRecon, setActivePaymentRecon] = useState<PaymentReconRow | null>(null);
  const [paymentReconMode, setPaymentReconMode] = useState<'bank_direct' | 'payment_tx'>('bank_direct');
  const [paymentReconReasonCode, setPaymentReconReasonCode] = useState<PaymentReconReasonCode>('partial_stripe');
  const [paymentReconReasonDetail, setPaymentReconReasonDetail] = useState('');
  const [selectedReconBankTxId, setSelectedReconBankTxId] = useState('');
  const [selectedReconPaymentTxId, setSelectedReconPaymentTxId] = useState('');
  const [paymentReconAllocationAmount, setPaymentReconAllocationAmount] = useState('');
  const [confirmCrossClientAllocation, setConfirmCrossClientAllocation] = useState(false);
  const [savingPaymentRecon, setSavingPaymentRecon] = useState(false);
  const [paymentTxCandidates, setPaymentTxCandidates] = useState<PaymentMeta[]>([]);

  const buildIssueFilters = useCallback((query: any) => {
    let q = query;
    if (statusFilter !== 'all') q = q.eq('status', statusFilter);
    if (severityFilter !== 'all') q = q.eq('severity', severityFilter);
    if (sourceFilter !== 'all') q = q.eq('source', sourceFilter);
    if (ownerFilter === 'unassigned') q = q.is('owner_user_id', null);
    else if (ownerFilter !== 'all') q = q.eq('owner_user_id', ownerFilter);
    if (typeFilter !== 'all') q = q.eq('issue_type', typeFilter);
    if (slaFilter !== 'all') {
      const dt = new Date();
      dt.setHours(dt.getHours() - (slaFilter === 'over48' ? 48 : 24));
      q = q.lte('opened_at', dt.toISOString());
    }
    if (issueOpenedFrom) {
      const fromDate = new Date(`${issueOpenedFrom}T00:00:00`);
      if (!Number.isNaN(fromDate.getTime())) q = q.gte('opened_at', fromDate.toISOString());
    }
    if (issueOpenedTo) {
      const toDate = new Date(`${issueOpenedTo}T23:59:59.999`);
      if (!Number.isNaN(toDate.getTime())) q = q.lte('opened_at', toDate.toISOString());
    }
    return q;
  }, [statusFilter, severityFilter, sourceFilter, ownerFilter, typeFilter, slaFilter, issueOpenedFrom, issueOpenedTo]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const todayIso = new Date().toISOString().slice(0, 10);
      const issuesBaseQuery = buildIssueFilters((supabase.from('finance_recon_issues' as any) as any).select('*'));
      const totalBaseQuery = buildIssueFilters((supabase.from('finance_recon_issues' as any) as any).select('id', { count: 'exact', head: true }));
      const statsBaseQuery = buildIssueFilters(
        (supabase.from('finance_recon_issues' as any) as any)
          .select('id,status,severity,opened_at,resolved_at,owner_user_id')
          .limit(1200),
      );

      const [issuesRes, totalRes, statsRes, bankTxRes, ownersRes, auditRes, authRes, matchCountRes, paymentsRes] = await Promise.all([
        issuesBaseQuery.order('opened_at', { ascending: false }).range((currentPage - 1) * PAGE_SIZE, (currentPage * PAGE_SIZE) - 1),
        totalBaseQuery,
        statsBaseQuery,
        (supabase.from('bank_transactions_normalized' as any) as any)
          .select('id, source, direction, booked_at, amount, currency, description, reference')
          .order('booked_at', { ascending: false })
          .limit(120),
        supabase
          .from('app_users')
          .select('id,display_name,first_name,last_name')
          .order('display_name', { ascending: true })
          .limit(200),
        (supabase.from('finance_recon_issue_audit' as any) as any)
          .select('id,issue_id,changed_at,changed_by_app_user_id,field_name,old_value,new_value,reason')
          .order('changed_at', { ascending: false })
          .limit(400),
        supabase.auth.getUser(),
        (supabase.from('finance_recon_matches' as any) as any).select('id', { count: 'exact', head: true }),
        supabase
          .from('payments')
          .select('id,deal_id,due_date,status,amount,paid_amount,balance,currency,payment_number,description')
          .in('status', ['pending', 'partially_paid', 'overdue'])
          .lte('due_date', todayIso)
          .order('due_date', { ascending: true })
          .limit(250),
      ]);

      if (issuesRes.error) throw issuesRes.error;
      if (totalRes.error) throw totalRes.error;
      if (statsRes.error) throw statsRes.error;
      if (bankTxRes.error) throw bankTxRes.error;
      if (ownersRes.error) throw ownersRes.error;
      if (auditRes.error) throw auditRes.error;
      if (paymentsRes.error) throw paymentsRes.error;

      setMatchTotalCount(matchCountRes.count || 0);

      const loadedIssues = ((issuesRes.data || []) as unknown) as ReconIssue[];
      const loadedStatsIssues = ((statsRes.data || []) as unknown) as ReconIssue[];
      const loadedOwners = (ownersRes.data || []) as OwnerRow[];

      setIssues(loadedIssues);
      setStatsIssues(loadedStatsIssues);
      setTotalIssuesCount(totalRes.count || loadedIssues.length);
      setBankTxOptions(((bankTxRes.data || []) as unknown) as BankTxn[]);

      const ownersMap = loadedOwners.reduce<Record<string, string>>((acc, row) => {
        const fallbackName = `${row.first_name || ''} ${row.last_name || ''}`.trim();
        acc[row.id] = row.display_name || fallbackName || 'Sem nome';
        return acc;
      }, {});

      setOwnerNames(ownersMap);
      setAssignees(loadedOwners.map((row) => ({ id: row.id, name: ownersMap[row.id] })));
      const auditRows = ((auditRes.data || []) as unknown) as IssueAuditRow[];
      const groupedAudit = auditRows.reduce<Record<string, IssueAuditRow[]>>((acc, row) => {
        if (!acc[row.issue_id]) acc[row.issue_id] = [];
        acc[row.issue_id].push(row);
        return acc;
      }, {});
      setAuditByIssue(groupedAudit);

      const paymentTxIds = Array.from(new Set(loadedIssues.map((i) => i.payment_transaction_id).filter(Boolean))) as string[];
      const bankTxIdsFromIssues = Array.from(new Set(loadedIssues.map((i) => i.bank_transaction_id).filter(Boolean))) as string[];

      if (paymentTxIds.length > 0) {
        const { data: paymentMetaRows, error: paymentMetaError } = await (supabase.from('payment_transactions' as any) as any)
          .select(`
            id,
            payment_id,
            deal_id,
            contact_id,
            transaction_date,
            amount,
            currency,
            payment_method,
            transaction_type,
            reference_number,
            status,
            recorded_by,
            confirmed_by,
            confirmed_at,
            deal:deals(name),
            contact:contacts(first_name,last_name),
            payment:payments(due_date,status,amount,paid_amount,balance,payment_number,description)
          `)
          .in('id', paymentTxIds);

        if (paymentMetaError) throw paymentMetaError;

        const metaMap = ((paymentMetaRows || []) as any[]).reduce<Record<string, PaymentMeta>>((acc, row) => {
          const fullName = `${row?.contact?.first_name || ''} ${row?.contact?.last_name || ''}`.trim() || null;
          acc[row.id] = {
            id: row.id,
            payment_id: row.payment_id || null,
            deal_id: row.deal_id || null,
            contact_id: row.contact_id || null,
            transaction_date: row.transaction_date || null,
            amount: row.amount ?? null,
            currency: row.currency ?? null,
            payment_method: row.payment_method ?? null,
            transaction_type: row.transaction_type ?? null,
            reference_number: row.reference_number ?? null,
            transaction_status: row.status ?? null,
            recorded_by: row.recorded_by ?? null,
            confirmed_by: row.confirmed_by ?? null,
            confirmed_at: row.confirmed_at ?? null,
            deal_name: row?.deal?.name || null,
            contact_name: fullName,
            due_date: row?.payment?.due_date || null,
            payment_status: getEffectiveReceivableStatus({
              status: row?.payment?.status,
              dueDate: row?.payment?.due_date,
              amount: row?.payment?.amount,
              paidAmount: row?.payment?.paid_amount,
              balance: row?.payment?.balance,
            }),
            payment_amount: row?.payment?.amount ?? null,
            payment_paid_amount: row?.payment?.paid_amount ?? null,
            payment_balance: row?.payment?.balance ?? null,
            payment_number: row?.payment?.payment_number || null,
            payment_description: row?.payment?.description || null,
            remaining_allocatable: null,
            is_legacy_linked: false,
          };
          return acc;
        }, {});
        setPaymentMetaByTxId(metaMap);
      } else {
        setPaymentMetaByTxId({});
      }

      if (bankTxIdsFromIssues.length > 0) {
        const { data: issueBankRows, error: issueBankRowsError } = await (supabase.from('bank_transactions_normalized' as any) as any)
          .select('id,source,direction,booked_at,amount,currency,description,reference')
          .in('id', bankTxIdsFromIssues);
        if (issueBankRowsError) throw issueBankRowsError;
        const bankMap = ((issueBankRows || []) as BankTxn[]).reduce<Record<string, BankTxn>>((acc, row) => {
          acc[row.id] = row;
          return acc;
        }, {});
        setBankMetaById(bankMap);
      } else {
        setBankMetaById({});
      }

      const { data: matchesRows, error: matchesError } = await (supabase.from('finance_recon_matches' as any) as any)
        .select('id,source,payment_transaction_id,bank_transaction_id,matched_by,matched_at,notes')
        .order('matched_at', { ascending: false })
        .limit(1000);
      if (matchesError) throw matchesError;
      const matches = ((matchesRows || []) as unknown) as ReconMatchRow[];
      setMatchedPaymentTxIds(new Set(matches.map((m) => m.payment_transaction_id).filter(Boolean) as string[]));
      setMatchedBankTxIds(new Set(matches.map((m) => m.bank_transaction_id).filter(Boolean) as string[]));
      const matchMap = loadedIssues.reduce<Record<string, ReconMatchRow>>((acc, issue) => {
        const match = matches.find((m) => {
          if (issue.payment_transaction_id && issue.bank_transaction_id) {
            return m.payment_transaction_id === issue.payment_transaction_id && m.bank_transaction_id === issue.bank_transaction_id;
          }
          if (issue.payment_transaction_id) return m.payment_transaction_id === issue.payment_transaction_id;
          if (issue.bank_transaction_id) return m.bank_transaction_id === issue.bank_transaction_id;
          return false;
        });
        if (match) acc[issue.id] = match;
        return acc;
      }, {});
      setMatchByIssueId(matchMap);

      const paymentRows = (paymentsRes.data || []) as any[];
      const dealIds = Array.from(new Set(paymentRows.map((row) => row.deal_id).filter(Boolean))) as string[];
      const { data: dealsRows, error: dealsError } = dealIds.length > 0
        ? await supabase.from('deals').select('id,name,contact_id').in('id', dealIds)
        : { data: [] as any[], error: null as any };
      if (dealsError) throw dealsError;
      const dealMap = new Map<string, any>((dealsRows || []).map((row: any) => [row.id, row]));

      const contactIds = Array.from(new Set((dealsRows || []).map((row: any) => row.contact_id).filter(Boolean))) as string[];
      const { data: contactsRows, error: contactsError } = contactIds.length > 0
        ? await supabase.from('contacts').select('id,first_name,last_name').in('id', contactIds)
        : { data: [] as any[], error: null as any };
      if (contactsError) throw contactsError;
      const contactMap = new Map<string, any>((contactsRows || []).map((row: any) => [row.id, row]));

      const normalizedPaymentRows: PaymentReconRow[] = paymentRows.map((row: any) => {
        const deal = dealMap.get(row.deal_id);
        const contact = deal?.contact_id ? contactMap.get(deal.contact_id) : null;
        const fullName = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || null;
        return {
          id: row.id,
          deal_id: row.deal_id,
          contact_id: deal?.contact_id || null,
          due_date: row.due_date,
          status: getEffectiveReceivableStatus({
            status: row.status,
            dueDate: row.due_date,
            amount: row.amount,
            paidAmount: row.paid_amount,
            balance: row.balance,
          }),
          amount: Number(row.amount || 0),
          paid_amount: Number(row.paid_amount || 0),
          balance: Number(row.balance ?? (Number(row.amount || 0) - Number(row.paid_amount || 0))),
          currency: row.currency || 'USD',
          payment_number: row.payment_number || null,
          description: row.description || null,
          deal_name: deal?.name || null,
          contact_name: fullName,
        };
      });
      setPaymentReconRows(normalizedPaymentRows);

      const authUserId = authRes.data.user?.id || null;
      const matchedAppUser = authUserId ? loadedOwners.find((owner) => {
        const o = owner as OwnerRow & { auth_user_id?: string | null };
        return o.auth_user_id === authUserId;
      }) : null;
      let appUserId = matchedAppUser?.id || null;
      if (!appUserId && authUserId) {
        const { data: appUserData } = await supabase
          .from('app_users')
          .select('id')
          .eq('auth_user_id', authUserId)
          .maybeSingle();
        appUserId = appUserData?.id || null;
      }
      setCurrentAppUserId(appUserId);

      if (appUserId) {
        const { data: alertsData, error: alertsError } = await (supabase.from('finance_sla_alerts' as any) as any)
          .select('id,alert_bucket,status')
          .eq('owner_user_id', appUserId)
          .eq('status', 'new')
          .eq('alert_date', new Date().toISOString().slice(0, 10))
          .limit(500);
        if (alertsError) throw alertsError;
        const rows = ((alertsData || []) as unknown) as SlaAlertRow[];
        setSlaAlertSummary({
          totalNew: rows.length,
          over24New: rows.filter((r) => r.alert_bucket === 'over24').length,
          over48New: rows.filter((r) => r.alert_bucket === 'over48').length,
        });
      } else {
        setSlaAlertSummary({ totalNew: 0, over24New: 0, over48New: 0 });
      }
    } catch (error: any) {
      console.error('Error loading reconciliation data:', error);
      toast.error(getFriendlyFinanceError(error, 'Falha ao carregar conciliação'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, PAGE_SIZE, buildIssueFilters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const runAutoMatch = async () => {
    setRunningMatch(true);
    try {
      const { data, error } = await supabase.functions.invoke('reconcile-bank-transactions', {
        body: { mode: 'incremental' },
      });
      if (error) throw error;
      toast.success(`Auto-match concluído: ${data?.matched_count || 0} match(es), ${data?.issues_created || 0} issue(s)`);
      await loadData();
    } catch (error: any) {
      console.error('Auto-match error:', error);
      toast.error(getFriendlyFinanceError(error, 'Erro no auto-match'));
    } finally {
      setRunningMatch(false);
    }
  };

  const runSlaAlerts = async () => {
    setRunningAlerts(true);
    try {
      const { data, error } = await supabase.functions.invoke('process-finance-sla-alerts', { body: {} });
      if (error) throw error;
      toast.success(`Alertas SLA: push enviado ${data?.sent ?? 0} / ignorado ${data?.skipped ?? 0}`);
      await loadData();
    } catch (error: any) {
      console.error('SLA alerts generation error:', error);
      toast.error(getFriendlyFinanceError(error, 'Erro ao gerar alertas SLA'));
    } finally {
      setRunningAlerts(false);
    }
  };

  const ackSlaAlerts = async () => {
    if (!currentAppUserId) return;
    setAckingAlerts(true);
    try {
      const { data, error } = await (supabase.rpc('fn_ack_finance_sla_alerts_for_owner' as any, { p_owner_user_id: currentAppUserId }) as any);
      if (error) throw error;
      toast.success(`Alertas marcados como lidos: ${data ?? 0}`);
      await loadData();
    } catch (error: any) {
      console.error('SLA alerts ack error:', error);
      toast.error(getFriendlyFinanceError(error, 'Erro ao marcar alertas'));
    } finally {
      setAckingAlerts(false);
    }
  };

  const filteredIssues = useMemo(() => {
    return issues.filter((i) => {
      if (severityFilter !== 'all' && i.severity !== severityFilter) return false;
      if (statusFilter !== 'all' && i.status !== statusFilter) return false;
      if (sourceFilter !== 'all' && i.source !== sourceFilter) return false;
      if (ownerFilter !== 'all' && (i.owner_user_id || 'unassigned') !== ownerFilter) return false;
      if (typeFilter !== 'all' && i.issue_type !== typeFilter) return false;
      if (paymentMethodFilter !== 'all') {
        const paymentMeta = i.payment_transaction_id ? paymentMetaByTxId[i.payment_transaction_id] : null;
        const method = String(paymentMeta?.payment_method || '').toLowerCase();
        if (method !== paymentMethodFilter) return false;
      }
      if (slaFilter !== 'all') {
        const ageHours = getIssueAgeHours(i.opened_at);
        if (slaFilter === 'over24' && ageHours < 24) return false;
        if (slaFilter === 'over48' && ageHours < 48) return false;
      }
      const query = issueSearchTerm.trim().toLowerCase();
      if (query) {
        const paymentMeta = i.payment_transaction_id ? paymentMetaByTxId[i.payment_transaction_id] : null;
        const bankMeta = i.bank_transaction_id ? bankMetaById[i.bank_transaction_id] : null;
        const ownerName = i.owner_user_id ? ownerNames[i.owner_user_id] || '' : '';
        const haystack = `${i.issue_type} ${i.source} ${i.id} ${ownerName} ${paymentMeta?.deal_name || ''} ${paymentMeta?.contact_name || ''} ${bankMeta?.reference || ''} ${bankMeta?.description || ''}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      return true;
    });
  }, [issues, severityFilter, statusFilter, sourceFilter, ownerFilter, typeFilter, paymentMethodFilter, slaFilter, issueSearchTerm, ownerNames, paymentMetaByTxId, bankMetaById]);

  const filteredPaymentReconRows = useMemo(() => {
    const query = paymentSearchTerm.trim().toLowerCase();
    return paymentReconRows.filter((row) => {
      if (query) {
        const haystack = `${row.contact_name || ''} ${row.deal_name || ''} ${row.payment_number || ''} ${row.description || ''} ${row.id}`.toLowerCase();
        if (!haystack.includes(query)) return false;
      }
      if (paymentDueFrom && row.due_date && row.due_date < paymentDueFrom) return false;
      if (paymentDueTo && row.due_date && row.due_date > paymentDueTo) return false;
      return true;
    });
  }, [paymentReconRows, paymentSearchTerm, paymentDueFrom, paymentDueTo]);

  const paymentReconBuckets = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const dueToday: PaymentReconRow[] = [];
    const overdue: PaymentReconRow[] = [];
    for (const row of filteredPaymentReconRows) {
      if (!row.due_date) continue;
      if (row.due_date === today) {
        dueToday.push(row);
      } else if (row.due_date < today) {
        overdue.push(row);
      }
    }
    return { dueToday, overdue };
  }, [filteredPaymentReconRows]);

  const bankCandidatesForActivePayment = useMemo(() => {
    if (!activePaymentRecon) return [] as BankTxn[];
    const tolerance = Math.max(Math.abs(Number(activePaymentRecon.balance || 0)) * 0.1, 2);
    const minAmount = Math.max(Number(activePaymentRecon.balance || 0) - tolerance, 0);
    const maxAmount = Number(activePaymentRecon.balance || 0) + tolerance;
    return bankTxOptions
      .filter((row) => !matchedBankTxIds.has(row.id))
      .filter((row) => (activePaymentRecon.currency ? row.currency === activePaymentRecon.currency : true))
      .filter((row) => Math.abs(Number(row.amount || 0)) >= minAmount && Math.abs(Number(row.amount || 0)) <= maxAmount)
      .slice(0, 80);
  }, [activePaymentRecon, bankTxOptions, matchedBankTxIds]);

  const paymentMethodOptions = useMemo(() => {
    const values = new Set<string>();
    Object.values(paymentMetaByTxId).forEach((meta) => {
      const method = String(meta.payment_method || '').trim().toLowerCase();
      if (method) values.add(method);
    });
    return Array.from(values).sort();
  }, [paymentMetaByTxId]);

  const sortedIssues = useMemo(() => {
    const severityWeight: Record<ReconIssue['severity'], number> = { critical: 4, high: 3, medium: 2, low: 1 };
    const rows = [...filteredIssues];
    rows.sort((a, b) => {
      if (sortBy === 'opened_at') {
        return new Date(b.opened_at).getTime() - new Date(a.opened_at).getTime();
      }
      if (sortBy === 'severity') {
        return severityWeight[b.severity] - severityWeight[a.severity];
      }
      if (sortBy === 'amount') {
        const av = Math.abs(Number(a.amount_found ?? a.amount_expected ?? 0));
        const bv = Math.abs(Number(b.amount_found ?? b.amount_expected ?? 0));
        return bv - av;
      }
      return getIssueAgeHours(b.opened_at) - getIssueAgeHours(a.opened_at);
    });
    return rows;
  }, [filteredIssues, sortBy]);

  const totalPages = Math.max(1, Math.ceil(totalIssuesCount / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
    setSelectedIssueIds([]);
  }, [statusFilter, severityFilter, sourceFilter, ownerFilter, typeFilter, paymentMethodFilter, slaFilter, issueSearchTerm, issueOpenedFrom, issueOpenedTo, sortBy]);

  const applyPreset = (preset: 'attention' | 'orphans' | 'missing' | 'resolved_today' | 'ops' | 'audit' | 'critical') => {
    // Reset common filters
    setSourceFilter('all');
    setOwnerFilter('all');
    setPaymentMethodFilter('all');
    setIssueSearchTerm('');

    switch (preset) {
      case 'attention':
        setStatusFilter('open');
        setSeverityFilter('all');
        setTypeFilter('all');
        setSlaFilter('all');
        setSortBy('severity');
        // Will show critical/high + >24h via the combined sort
        break;
      case 'orphans':
        setStatusFilter('open');
        setSeverityFilter('all');
        setTypeFilter('orphan');
        setSlaFilter('all');
        setSortBy('sla');
        break;
      case 'missing':
        setStatusFilter('open');
        setSeverityFilter('all');
        setTypeFilter('missing');
        setSlaFilter('all');
        setSortBy('sla');
        break;
      case 'resolved_today':
        setStatusFilter('resolved');
        setSeverityFilter('all');
        setTypeFilter('all');
        setSlaFilter('all');
        setSortBy('opened_at');
        break;
      case 'ops':
        setStatusFilter('open');
        setSeverityFilter('all');
        setTypeFilter('all');
        setSlaFilter('all');
        setSortBy('sla');
        break;
      case 'audit':
        setStatusFilter('all');
        setSeverityFilter('all');
        setTypeFilter('all');
        setSlaFilter('over24');
        setSortBy('opened_at');
        break;
      case 'critical':
        setStatusFilter('open');
        setSeverityFilter('critical');
        setTypeFilter('all');
        setSlaFilter('over48');
        setSortBy('severity');
        break;
    }
  };

  const toggleIssueSelection = (issueId: string) => {
    setSelectedIssueIds((prev) => prev.includes(issueId) ? prev.filter((id) => id !== issueId) : [...prev, issueId]);
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = sortedIssues.map((row) => row.id);
    const allSelected = visibleIds.every((id) => selectedIssueIds.includes(id));
    if (allSelected) {
      setSelectedIssueIds((prev) => prev.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIssueIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const bulkUpdateStatus = async (status: 'in_review' | 'resolved' | 'waived') => {
    if (selectedIssueIds.length === 0) {
      toast.error('Selecione ao menos uma issue.');
      return;
    }
    setBulkUpdating(true);
    try {
      const payload: Record<string, any> = { status };
      if (status === 'resolved' || status === 'waived') {
        payload.resolved_at = new Date().toISOString();
      }
      const { error } = await supabase
        .from('finance_recon_issues' as any)
        .update(payload)
        .in('id', selectedIssueIds);
      if (error) throw error;
      toast.success(`Atualizadas ${selectedIssueIds.length} issue(s).`);
      setSelectedIssueIds([]);
      await loadData();
    } catch (error: any) {
      toast.error(getFriendlyFinanceError(error, 'Falha na ação em lote'));
    } finally {
      setBulkUpdating(false);
    }
  };

  const summary = useMemo(() => {
    const openRows = statsIssues.filter((i) => i.status === 'open' || i.status === 'in_review');
    const criticalCount = openRows.filter((i) => i.severity === 'critical').length;
    const highCount = openRows.filter((i) => i.severity === 'high').length;
    const criticalOpen = criticalCount + highCount;
    const over24h = openRows.filter((i) => getIssueAgeHours(i.opened_at) >= 24).length;
    const over48h = openRows.filter((i) => getIssueAgeHours(i.opened_at) >= 48).length;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const resolvedToday = statsIssues.filter((i) => {
      if (i.status !== 'resolved' || !i.resolved_at) return false;
      const resolvedAt = new Date(i.resolved_at).getTime();
      return Number.isFinite(resolvedAt) && resolvedAt >= startOfToday.getTime();
    }).length;

    const totalOpenAmount = openRows.reduce((sum, i) => {
      const val = typeof i.amount_expected === 'number' ? Math.abs(i.amount_expected) : (typeof i.amount_found === 'number' ? Math.abs(i.amount_found) : 0);
      return sum + val;
    }, 0);

    const openIssueCount = openRows.length;
    const matchRate = matchTotalCount + openIssueCount > 0
      ? Math.round((matchTotalCount / (matchTotalCount + openIssueCount)) * 100)
      : 100;

    // Determine predominant currency from open issues
    const currencyCounts: Record<string, number> = {};
    openRows.forEach((i) => {
      const c = i.currency || 'USD';
      currencyCounts[c] = (currencyCounts[c] || 0) + 1;
    });
    const predominantCurrency = Object.entries(currencyCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || 'USD';

    return {
      open: openIssueCount,
      criticalCount,
      highCount,
      criticalOpen,
      over24h,
      over48h,
      resolvedToday,
      totalOpenAmount,
      matchRate,
      predominantCurrency,
    };
  }, [statsIssues, matchTotalCount]);

  const ownerSla = useMemo(() => {
    const openRows = statsIssues.filter((i) => i.status === 'open' || i.status === 'in_review');
    const grouped: Record<string, { ownerLabel: string; open: number; over24h: number; over48h: number; critical: number }> = {};

    for (const issue of openRows) {
      const ownerId = issue.owner_user_id || 'unassigned';
      const ownerLabel = issue.owner_user_id ? ownerNames[issue.owner_user_id] || 'Sem nome' : 'Sem responsável';
      if (!grouped[ownerId]) {
        grouped[ownerId] = { ownerLabel, open: 0, over24h: 0, over48h: 0, critical: 0 };
      }

      grouped[ownerId].open += 1;
      const ageHours = getIssueAgeHours(issue.opened_at);
      if (ageHours >= 24) grouped[ownerId].over24h += 1;
      if (ageHours >= 48) grouped[ownerId].over48h += 1;
      if (issue.severity === 'critical' || issue.severity === 'high') grouped[ownerId].critical += 1;
    }

    return Object.values(grouped).sort(
      (a, b) => (b.over48h - a.over48h) || (b.over24h - a.over24h) || (b.critical - a.critical) || (b.open - a.open)
    );
  }, [statsIssues, ownerNames]);

  const openIssueSheet = async (issue: ReconIssue) => {
    setActiveIssueId(issue.id);
    setSelectedBankTxnId(issue.bank_transaction_id || '');
    setSelectedPaymentId('');
    setSelectedPaymentTransactionId('');
    setManualNote(issue.resolution_note || '');
    setManualBankScope('compatible');
    setSheetOpen(true);
    setCandidateBankTxns([]);
    setCandidatePaymentTxns([]);
    setCandidateReceivables([]);

    // Load candidates for guided matching
    if (issue.issue_type === 'missing' && issue.payment_transaction_id) {
      const meta = paymentMetaByTxId[issue.payment_transaction_id];
      if (meta) {
        const amt = Number(meta.amount || 0);
        const tolerance = amt * 0.05;
        const candidates = bankTxOptions.filter((btx) => {
          if (issue.currency && btx.currency !== issue.currency) return false;
          if (Math.abs(Number(btx.amount) - amt) > tolerance) return false;
          if (meta.transaction_date) {
            const daysDiff = Math.abs(new Date(btx.booked_at).getTime() - new Date(meta.transaction_date).getTime()) / 86400000;
            if (daysDiff > 7) return false;
          }
          return true;
        });
        // Sort by date proximity
        candidates.sort((a, b) => {
          if (!meta.transaction_date) return 0;
          const aDiff = Math.abs(new Date(a.booked_at).getTime() - new Date(meta.transaction_date).getTime());
          const bDiff = Math.abs(new Date(b.booked_at).getTime() - new Date(meta.transaction_date).getTime());
          return aDiff - bDiff;
        });
        setCandidateBankTxns(candidates);
      }
    } else if (issue.issue_type === 'orphan' && issue.bank_transaction_id) {
      const bankMeta = bankMetaById[issue.bank_transaction_id];
      if (bankMeta) {
        const amt = Number(bankMeta.amount || 0);
        const tolerance = Math.abs(amt) * 0.05;
        const minAmt = amt - tolerance;
        const maxAmt = amt + tolerance;
        const refDate = new Date(bankMeta.booked_at);
        const dateMin = new Date(refDate.getTime() - 7 * 86400000).toISOString().slice(0, 10);
        const dateMax = new Date(refDate.getTime() + 7 * 86400000).toISOString().slice(0, 10);

        let query = (supabase.from('payment_transactions' as any) as any)
          .select('id, payment_id, deal_id, contact_id, transaction_date, amount, currency, payment_method, transaction_type, reference_number, status, recorded_by, confirmed_by, confirmed_at, deal:deals(name), contact:contacts(first_name,last_name)')
          .eq('status', 'confirmed')
          .gte('amount', minAmt)
          .lte('amount', maxAmt)
          .gte('transaction_date', dateMin)
          .lte('transaction_date', dateMax)
          .limit(20);
        if (issue.currency) query = query.eq('currency', issue.currency);

        const { data: ptRows } = await query;
        const candidates = ((ptRows || []) as any[]).map((row: any) => {
          const fullName = `${row?.contact?.first_name || ''} ${row?.contact?.last_name || ''}`.trim() || null;
          return {
            id: row.id,
            payment_id: row.payment_id || null,
            deal_id: row.deal_id || null,
            contact_id: row.contact_id || null,
            transaction_date: row.transaction_date || null,
            amount: row.amount ?? null,
            currency: row.currency ?? null,
            payment_method: row.payment_method ?? null,
            transaction_type: row.transaction_type ?? null,
            reference_number: row.reference_number ?? null,
            transaction_status: row.status ?? null,
            recorded_by: row.recorded_by ?? null,
            confirmed_by: row.confirmed_by ?? null,
            confirmed_at: row.confirmed_at ?? null,
            deal_name: row?.deal?.name || null,
            contact_name: fullName,
            due_date: null,
            payment_status: null,
            payment_amount: null,
            payment_paid_amount: null,
            payment_balance: null,
            payment_number: null,
            payment_description: null,
            remaining_allocatable: null,
            is_legacy_linked: false,
          } as PaymentMeta;
        });
        setCandidatePaymentTxns(candidates);

        if (bankMeta.direction === 'in') {
          const expectedAmount = Math.abs(Number(bankMeta.amount || 0));
          const tolerance = Math.max(expectedAmount * 0.05, 1);
          const { data: openPaymentsRows } = await (supabase.from('payments' as any) as any)
            .select('id,deal_id,due_date,status,amount,paid_amount,balance,currency,payment_number,description')
            .in('status', ['pending', 'partially_paid', 'overdue'])
            .gte('balance', Math.max(expectedAmount - tolerance, 0))
            .lte('balance', expectedAmount + tolerance)
            .order('due_date', { ascending: true })
            .limit(60);

          const paymentRows = (openPaymentsRows || []) as any[];
          if (paymentRows.length > 0) {
            const dealIds = Array.from(new Set(paymentRows.map((row) => row.deal_id).filter(Boolean))) as string[];
            const { data: dealsRows } = dealIds.length > 0
              ? await supabase.from('deals').select('id,name,contact_id').in('id', dealIds)
              : { data: [] as any[] };
            const dealMap = new Map<string, any>((dealsRows || []).map((row: any) => [row.id, row]));

            const contactIds = Array.from(new Set((dealsRows || []).map((row: any) => row.contact_id).filter(Boolean))) as string[];
            const { data: contactsRows } = contactIds.length > 0
              ? await supabase.from('contacts').select('id,first_name,last_name').in('id', contactIds)
              : { data: [] as any[] };
            const contactMap = new Map<string, any>((contactsRows || []).map((row: any) => [row.id, row]));

            const receivableRows: ReceivableCandidate[] = paymentRows.map((row: any) => {
              const deal = dealMap.get(row.deal_id);
              const contact = deal?.contact_id ? contactMap.get(deal.contact_id) : null;
              const fullName = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || null;
              return {
                id: row.id,
                deal_id: row.deal_id,
                due_date: row.due_date,
                status: getEffectiveReceivableStatus({
                  status: row.status,
                  dueDate: row.due_date,
                  amount: row.amount,
                  paidAmount: row.paid_amount,
                  balance: row.balance,
                }),
                amount: Number(row.amount || 0),
                paid_amount: Number(row.paid_amount || 0),
                balance: Number(row.balance || 0),
                currency: row.currency || null,
                payment_number: row.payment_number || null,
                description: row.description || null,
                deal_name: deal?.name || null,
                contact_name: fullName,
              };
            });
            setCandidateReceivables(receivableRows);
          }
        }
      }
    }
  };

  const selectedIssue = useMemo(
    () => (activeIssueId ? issues.find((i) => i.id === activeIssueId) || null : null),
    [activeIssueId, issues],
  );

  const eligibleBankTxOptions = useMemo(() => {
    if (!selectedIssue) return bankTxOptions;
    const paymentMeta = selectedIssue.payment_transaction_id ? paymentMetaByTxId[selectedIssue.payment_transaction_id] || null : null;
    const expectedDirection = inferExpectedDirection(selectedIssue, paymentMeta);

    return bankTxOptions.filter((tx) => {
      if (selectedIssue.currency && tx.currency !== selectedIssue.currency) return false;
      if (expectedDirection && tx.direction !== expectedDirection) return false;
      return true;
    });
  }, [selectedIssue, bankTxOptions, paymentMetaByTxId]);

  const manualBankOptions = useMemo(() => {
    return manualBankScope === 'all' ? bankTxOptions : eligibleBankTxOptions;
  }, [manualBankScope, bankTxOptions, eligibleBankTxOptions]);

  const updateIssueStatus = async (
    issueId: string,
    status: 'open' | 'in_review' | 'resolved' | 'waived',
    resolutionNote?: string | null,
  ) => {
    const payload: Record<string, any> = { status };
    if (status === 'resolved' || status === 'waived') {
      payload.resolved_at = new Date().toISOString();
      if (resolutionNote) payload.resolution_note = resolutionNote;
    } else {
      payload.resolved_at = null;
      if (status === 'open') payload.resolution_note = null;
    }

    const { error } = await supabase
      .from('finance_recon_issues' as any)
      .update(payload)
      .eq('id', issueId);

    if (error) throw error;
  };

  const assignIssueOwner = async (issueId: string, ownerUserId: string | null) => {
    const { error } = await supabase
      .from('finance_recon_issues' as any)
      .update({ owner_user_id: ownerUserId })
      .eq('id', issueId);

    if (error) throw error;
  };

  const saveManualMatch = async () => {
    if (!activeIssueId || !selectedBankTxnId || !manualNote.trim()) {
      toast.error('Selecione transação bancária e informe motivo');
      return;
    }

    setSavingManualMatch(true);
    try {
      const targetIssue = issues.find((i) => i.id === activeIssueId);
      if (!targetIssue) throw new Error('Issue não encontrada');
      const targetBankTx = bankTxOptions.find((tx) => tx.id === selectedBankTxnId) || null;
      if (!targetBankTx) throw new Error('Transação bancária não encontrada');
      const targetPaymentMeta = targetIssue.payment_transaction_id ? paymentMetaByTxId[targetIssue.payment_transaction_id] || null : null;
      const expectedDirection = inferExpectedDirection(targetIssue, targetPaymentMeta);
      const directionMismatch = Boolean(expectedDirection && targetBankTx.direction !== expectedDirection);
      const mismatchNote = directionMismatch
        ? ` [override-direcao: esperado=${expectedDirection === 'in' ? 'entrada' : 'saída'} selecionado=${targetBankTx.direction === 'in' ? 'entrada' : 'saída'}]`
        : '';
      const finalManualNote = `${manualNote.trim()}${mismatchNote}`;

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();

      if (!targetIssue.payment_transaction_id && !selectedPaymentId && !selectedPaymentTransactionId) {
        toast.error('Selecione um recebível ou uma transação de pagamento para vincular');
        return;
      }

      const { error: reconcileError } = await (supabase.rpc('fn_finance_manual_reconcile_issue' as any, {
        p_issue_id: activeIssueId,
        p_bank_transaction_id: selectedBankTxnId,
        p_reason: finalManualNote,
        p_payment_id: selectedPaymentId || null,
        p_payment_transaction_id: targetIssue.payment_transaction_id || selectedPaymentTransactionId || null,
        p_matched_by_app_user_id: appUser?.id || null,
      }) as any);
      if (reconcileError) {
        const maybeMissingFn = String(reconcileError?.message || '').toLowerCase().includes('fn_finance_manual_reconcile_issue');
        if (!maybeMissingFn) throw reconcileError;

        // Temporary fallback for environments where DB migration is not yet applied.
        const { error: matchError } = await supabase
          .from('finance_recon_matches' as any)
          .insert({
            status: 'matched',
            source: targetBankTx.source || targetIssue.source,
            payment_transaction_id: targetIssue.payment_transaction_id || selectedPaymentTransactionId || null,
            bank_transaction_id: selectedBankTxnId,
            stripe_event_id: targetIssue.stripe_event_id || null,
            matched_by: appUser?.id || null,
            notes: `${finalManualNote} [fallback-sem-rpc]`,
          });
        if (matchError) throw matchError;

        const { error: issueError } = await supabase
          .from('finance_recon_issues' as any)
          .update({
            status: 'resolved',
            resolved_at: new Date().toISOString(),
            resolution_note: `${finalManualNote} [fallback-sem-rpc]`,
            bank_transaction_id: selectedBankTxnId,
          })
          .eq('id', activeIssueId);
        if (issueError) throw issueError;
      }

      toast.success('Issue conciliada manualmente');
      setActiveIssueId(null);
      setSelectedBankTxnId('');
      setSelectedPaymentId('');
      setSelectedPaymentTransactionId('');
      setManualNote('');
      setSheetOpen(false);
      await loadData();
    } catch (error: any) {
      console.error('Manual match error:', error);
      toast.error(getFriendlyFinanceError(error, 'Erro ao salvar match manual'));
    } finally {
      setSavingManualMatch(false);
    }
  };

  const loadPaymentTxCandidates = useCallback(async (payment: PaymentReconRow) => {
    const maxAmount = Math.max(Number(payment.balance || 0), 0);
    if (maxAmount <= 0) return [] as PaymentMeta[];

    let query = (supabase.from('payment_transactions' as any) as any)
      .select('id,payment_id,deal_id,contact_id,transaction_date,amount,currency,payment_method,transaction_type,reference_number,status,recorded_by,confirmed_by,confirmed_at,deal:deals(name),contact:contacts(first_name,last_name),payment:payments(due_date,status,amount,paid_amount,balance,payment_number,description)')
      .eq('status', 'confirmed')
      .eq('payment_method', 'stripe')
      .gt('amount', 0)
      .lte('amount', maxAmount)
      .order('transaction_date', { ascending: false })
      .limit(80);

    if (payment.currency) query = query.eq('currency', payment.currency);

    const { data: txRows } = await query;
    const mappedCandidates = ((txRows || []) as any[]).map((row: any) => {
      const fullName = `${row?.contact?.first_name || ''} ${row?.contact?.last_name || ''}`.trim() || null;
      return {
        id: row.id,
        payment_id: row.payment_id || null,
        deal_id: row.deal_id || null,
        contact_id: row.contact_id || null,
        transaction_date: row.transaction_date || null,
        amount: row.amount ?? null,
        currency: row.currency ?? null,
        payment_method: row.payment_method ?? null,
        transaction_type: row.transaction_type ?? null,
        reference_number: row.reference_number ?? null,
        transaction_status: row.status ?? null,
        recorded_by: row.recorded_by ?? null,
        confirmed_by: row.confirmed_by ?? null,
        confirmed_at: row.confirmed_at ?? null,
        deal_name: row?.deal?.name || null,
        contact_name: fullName,
        due_date: row?.payment?.due_date || null,
        payment_status: getEffectiveReceivableStatus({
          status: row?.payment?.status,
          dueDate: row?.payment?.due_date,
          amount: row?.payment?.amount,
          paidAmount: row?.payment?.paid_amount,
          balance: row?.payment?.balance,
        }),
        payment_amount: row?.payment?.amount ?? null,
        payment_paid_amount: row?.payment?.paid_amount ?? null,
        payment_balance: row?.payment?.balance ?? null,
        payment_number: row?.payment?.payment_number || null,
        payment_description: row?.payment?.description || null,
        remaining_allocatable: null,
        is_legacy_linked: false,
      } as PaymentMeta;
    });

    const candidateIds = mappedCandidates.map((row) => row.id);
    const matchedByQuery = new Set<string>();
    const allocatedByTxId: Record<string, number> = {};
    if (candidateIds.length > 0) {
      const { data: matchedRows } = await (supabase.from('finance_recon_matches' as any) as any)
        .select('payment_transaction_id,status')
        .in('payment_transaction_id', candidateIds)
        .eq('status', 'matched');
      ((matchedRows || []) as any[]).forEach((row) => {
        if (row.payment_transaction_id) matchedByQuery.add(row.payment_transaction_id);
      });

      const { data: allocationRows } = await (supabase.from('payment_transaction_allocations' as any) as any)
        .select('payment_transaction_id,amount_allocated')
        .in('payment_transaction_id', candidateIds);
      ((allocationRows || []) as any[]).forEach((row) => {
        const txId = row.payment_transaction_id as string | null;
        if (!txId) return;
        allocatedByTxId[txId] = Number(allocatedByTxId[txId] || 0) + Number(row.amount_allocated || 0);
      });
    }

    return mappedCandidates
      .map((row) => {
        const allocated = Number(allocatedByTxId[row.id] || 0);
        const hasLegacyMatchNoAllocation = matchedByQuery.has(row.id) && allocated <= 0;
        const linkedPaymentStatus = String(row.payment_status || '').toLowerCase();
        const isLinkedAndSettled =
          Boolean(row.payment_id) &&
          (linkedPaymentStatus === 'paid' || linkedPaymentStatus === 'cancelled');

        let remaining = 0;
        if (allocated > 0) {
          remaining = Math.max(Number(row.amount || 0) - allocated, 0);
        } else if (isLinkedAndSettled) {
          remaining = 0;
        } else if (hasLegacyMatchNoAllocation && !row.payment_id) {
          remaining = 0;
        } else {
          remaining = Math.max(Number(row.amount || 0), 0);
        }

        const isLegacyLinked = Boolean(row.payment_id) && allocated <= 0;
        return {
          ...row,
          remaining_allocatable: remaining,
          is_legacy_linked: isLegacyLinked,
        } as PaymentMeta;
      })
      .filter((row) => Number(row.remaining_allocatable || 0) > 0)
      .sort((a, b) => {
        const aPriority =
          (payment.deal_id && a.deal_id === payment.deal_id ? 2 : 0) +
          (payment.contact_id && a.contact_id === payment.contact_id ? 1 : 0);
        const bPriority =
          (payment.deal_id && b.deal_id === payment.deal_id ? 2 : 0) +
          (payment.contact_id && b.contact_id === payment.contact_id ? 1 : 0);
        if (aPriority !== bPriority) return bPriority - aPriority;

        const aDate = a.transaction_date ? new Date(a.transaction_date).getTime() : 0;
        const bDate = b.transaction_date ? new Date(b.transaction_date).getTime() : 0;
        return bDate - aDate;
      });
  }, []);

  const openPaymentReconciliation = async (payment: PaymentReconRow, mode: 'bank_direct' | 'payment_tx') => {
    setActivePaymentRecon(payment);
    setPaymentReconMode(mode);
    setPaymentReconReasonCode(mode === 'payment_tx' ? 'partial_stripe' : 'manual_override');
    setPaymentReconReasonDetail('');
    setSelectedReconBankTxId('');
    setSelectedReconPaymentTxId('');
    setPaymentReconAllocationAmount('');
    setConfirmCrossClientAllocation(false);
    setPaymentTxCandidates([]);
    setPaymentReconSheetOpen(true);

    if (mode === 'payment_tx') {
      const candidates = await loadPaymentTxCandidates(payment);
      setPaymentTxCandidates(candidates);
    }
  };

  useEffect(() => {
    const loadCandidates = async () => {
      if (!paymentReconSheetOpen || !activePaymentRecon || paymentReconMode !== 'payment_tx') return;
      const candidates = await loadPaymentTxCandidates(activePaymentRecon);
      setPaymentTxCandidates(candidates);
    };

    loadCandidates();
  }, [paymentReconSheetOpen, activePaymentRecon, paymentReconMode, loadPaymentTxCandidates]);

  const savePaymentReconciliation = async () => {
    if (!activePaymentRecon) return;
    if (!paymentReconReasonCode) {
      toast.error('Informe o motivo da conciliação');
      return;
    }
    if (paymentReconMode === 'bank_direct' && !selectedReconBankTxId) {
      toast.error('Selecione uma transação bancária');
      return;
    }
    if (paymentReconMode === 'payment_tx' && !selectedReconPaymentTxId) {
      toast.error('Selecione uma transação de pagamento');
      return;
    }
    if (paymentReconMode === 'payment_tx' && !paymentReconAllocationAmount.trim()) {
      toast.error('Informe o valor da alocação');
      return;
    }

    setSavingPaymentRecon(true);
    try {
      const serializedReason = paymentReconReasonDetail.trim()
        ? `${paymentReconReasonCode} | ${paymentReconReasonDetail.trim()}`
        : paymentReconReasonCode;

      const { data: appUser } = await supabase
        .from('app_users')
        .select('id')
        .eq('auth_user_id', (await supabase.auth.getUser()).data.user?.id)
        .maybeSingle();

      if (paymentReconMode === 'payment_tx') {
        const selectedTx = paymentTxCandidates.find((tx) => tx.id === selectedReconPaymentTxId);
        if (!selectedTx) {
          throw new Error('Transação selecionada não encontrada');
        }

        const allocation = Number(String(paymentReconAllocationAmount).replace(',', '.'));
        if (!Number.isFinite(allocation) || allocation <= 0) {
          throw new Error('Valor de alocação inválido');
        }
        const txRemaining = Number(selectedTx.remaining_allocatable || 0);
        const paymentBalance = Number(activePaymentRecon.balance || 0);
        const maxAlloc = Math.min(txRemaining, paymentBalance);
        if (allocation > maxAlloc) {
          throw new Error(`Alocação acima do permitido (máximo ${maxAlloc.toFixed(2)})`);
        }

        const isCrossClient = Boolean(
          (selectedTx.deal_id && activePaymentRecon.deal_id && selectedTx.deal_id !== activePaymentRecon.deal_id) ||
          (selectedTx.contact_id && activePaymentRecon.contact_id && selectedTx.contact_id !== activePaymentRecon.contact_id)
        );
        if (isCrossClient) {
          if (!confirmCrossClientAllocation) {
            throw new Error('Confirme a alocação cross-client antes de conciliar');
          }
          if (!['manual_override', 'cross_client_allocation'].includes(paymentReconReasonCode)) {
            throw new Error('Cross-client exige motivo manual_override ou cross_client_allocation');
          }
          if (!paymentReconReasonDetail.trim()) {
            throw new Error('Cross-client exige detalhe de auditoria');
          }
        }
        if (selectedTx.is_legacy_linked) {
          if (!['manual_override', 'cross_client_allocation'].includes(paymentReconReasonCode)) {
            throw new Error('Transação legado vinculada exige motivo manual_override ou cross_client_allocation');
          }
          if (!paymentReconReasonDetail.trim()) {
            throw new Error('Transação legado vinculada exige detalhe de auditoria');
          }
        }
        if (paymentReconReasonCode === 'other' && !paymentReconReasonDetail.trim()) {
          throw new Error('Motivo "Outro" exige detalhe');
        }

        const { data, error } = await (supabase.rpc('fn_finance_allocate_payment_transaction' as any, {
          p_payment_transaction_id: selectedTx.id,
          p_payment_id: activePaymentRecon.id,
          p_amount: allocation,
          p_reason_code: paymentReconReasonCode,
          p_reason_detail: paymentReconReasonDetail.trim() || null,
          p_confirm_cross_client: isCrossClient ? confirmCrossClientAllocation : false,
          p_idempotency_key: null,
          p_actor_app_user_id: appUser?.id || null,
        }) as any);
        if (error) throw error;

        const result = (data || {}) as Record<string, any>;
        const nextBalance = Number(result.new_balance ?? activePaymentRecon.balance);
        const nextPaidAmount = Number(result.new_paid_amount ?? activePaymentRecon.paid_amount);
        const nextStatus = String(result.new_status || activePaymentRecon.status);
        const updatedPayment: PaymentReconRow = {
          ...activePaymentRecon,
          balance: Number.isFinite(nextBalance) ? nextBalance : activePaymentRecon.balance,
          paid_amount: Number.isFinite(nextPaidAmount) ? nextPaidAmount : activePaymentRecon.paid_amount,
          status: nextStatus,
        };

        setActivePaymentRecon(updatedPayment);
        setSelectedReconPaymentTxId('');
        setPaymentReconAllocationAmount('');
        setConfirmCrossClientAllocation(false);
        setPaymentReconReasonCode('partial_stripe');
        setPaymentReconReasonDetail('');
        await loadData();

        if ((updatedPayment.balance || 0) <= 0) {
          toast.success('Parcela quitada com sucesso');
          setPaymentReconSheetOpen(false);
          setActivePaymentRecon(null);
          return;
        }

        const refreshedCandidates = await loadPaymentTxCandidates(updatedPayment);
        setPaymentTxCandidates(refreshedCandidates);
        toast.success('Alocação parcial aplicada com sucesso');
      } else {
        const { error } = await (supabase.rpc('fn_finance_reconcile_payment' as any, {
          p_payment_id: activePaymentRecon.id,
          p_mode: paymentReconMode,
          p_bank_transaction_id: selectedReconBankTxId,
          p_payment_transaction_id: null,
          p_reason: serializedReason,
          p_actor_app_user_id: appUser?.id || null,
        }) as any);
        if (error) throw error;

        toast.success('Pagamento conciliado com sucesso');
        setPaymentReconSheetOpen(false);
        setActivePaymentRecon(null);
        await loadData();
      }
    } catch (error: any) {
      console.error('Payment reconciliation error:', error);
      toast.error(getFriendlyFinanceError(error, 'Falha ao conciliar pagamento'));
    } finally {
      setSavingPaymentRecon(false);
    }
  };

  const exportCsv = () => {
    const csvEscape = (val: unknown) => {
      const s = String(val ?? '');
      if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
      return s;
    };
    const headers = ['ID', 'Tipo', 'Severidade', 'Status', 'Valor Esperado', 'Valor Encontrado', 'Moeda', 'Data Abertura', 'Resolvida em', 'Responsável', 'Cliente', 'Deal', 'Método Pgto', 'Fonte', 'Nota'];
    const rows = sortedIssues.map((issue) => {
      const meta = issue.payment_transaction_id ? paymentMetaByTxId[issue.payment_transaction_id] : null;
      return [
        issue.id.slice(0, 8),
        issue.issue_type,
        SEVERITY_LABEL[issue.severity],
        STATUS_LABEL[issue.status],
        issue.amount_expected ?? '',
        issue.amount_found ?? '',
        issue.currency || '',
        new Date(issue.opened_at).toLocaleDateString(),
        issue.resolved_at ? new Date(issue.resolved_at).toLocaleDateString() : '',
        issue.owner_user_id ? (ownerNames[issue.owner_user_id] || '') : '',
        meta?.contact_name || '',
        meta?.deal_name || '',
        meta?.payment_method ? (PAYMENT_METHOD_LABEL[meta.payment_method] || meta.payment_method) : '',
        SOURCE_LABEL[issue.source] || issue.source,
        issue.resolution_note || '',
      ];
    });
    const csvContent = [headers.map(csvEscape).join(','), ...rows.map((r) => r.map(csvEscape).join(','))].join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `reconciliacao_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const severityClass = (severity: ReconIssue['severity']) => {
    if (severity === 'critical') return 'bg-red-100 text-red-700 border-red-300';
    if (severity === 'high') return 'bg-orange-100 text-orange-700 border-orange-300';
    if (severity === 'medium') return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    return 'bg-slate-100 text-slate-700 border-slate-300';
  };

  const selectedReconPaymentTx = useMemo(
    () => paymentTxCandidates.find((tx) => tx.id === selectedReconPaymentTxId) || null,
    [paymentTxCandidates, selectedReconPaymentTxId]
  );
  const isCrossClientSelection = useMemo(() => {
    if (!selectedReconPaymentTx || !activePaymentRecon) return false;
    return Boolean(
      (selectedReconPaymentTx.deal_id && activePaymentRecon.deal_id && selectedReconPaymentTx.deal_id !== activePaymentRecon.deal_id) ||
      (selectedReconPaymentTx.contact_id && activePaymentRecon.contact_id && selectedReconPaymentTx.contact_id !== activePaymentRecon.contact_id)
    );
  }, [selectedReconPaymentTx, activePaymentRecon]);
  const maxAllocatableForSelection = useMemo(() => {
    if (!selectedReconPaymentTx || !activePaymentRecon) return 0;
    return Math.max(
      Math.min(
        Number(selectedReconPaymentTx.remaining_allocatable || 0),
        Number(activePaymentRecon.balance || 0)
      ),
      0
    );
  }, [selectedReconPaymentTx, activePaymentRecon]);

  return (
    <FinancePageLayout>
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Conciliação</h1>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
            Atualizar
          </Button>
          <Button variant="outline" onClick={runSlaAlerts} disabled={runningAlerts}>
            {runningAlerts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Processar Push SLA
          </Button>
          <Button onClick={runAutoMatch} disabled={runningMatch}>
            {runningMatch ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Rodar Auto-Match
          </Button>
          <Button variant="outline" onClick={exportCsv} disabled={sortedIssues.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Alertas SLA de Hoje (meus)</CardTitle>
          <Button variant="outline" size="sm" onClick={ackSlaAlerts} disabled={ackingAlerts || slaAlertSummary.totalNew === 0 || !currentAppUserId}>
            {ackingAlerts ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Marcar como lidos
          </Button>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Novos (total)</p>
            <p className="text-2xl font-bold">{slaAlertSummary.totalNew}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Bucket &gt;24h</p>
            <p className="text-2xl font-bold text-orange-600">{slaAlertSummary.over24New}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Bucket &gt;48h</p>
            <p className="text-2xl font-bold text-red-600">{slaAlertSummary.over48New}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Indicadores Globais</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-7 gap-3">
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Abertas</p>
            <p className="text-2xl font-bold">{summary.open}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Criticas</p>
            <p className="text-2xl font-bold text-red-600">{summary.criticalCount}</p>
            <p className="text-xs text-muted-foreground">Altas: {summary.highCount}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">&gt;24h</p>
            <p className="text-2xl font-bold text-orange-600">{summary.over24h}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">&gt;48h</p>
            <p className="text-2xl font-bold text-red-600">{summary.over48h}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Valor total aberto</p>
            <p className="text-2xl font-bold">{formatMoney(summary.totalOpenAmount, (summary.predominantCurrency as 'USD' | 'BRL') || 'USD')}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Taxa de match</p>
            <p className={`text-2xl font-bold ${summary.matchRate >= 90 ? 'text-emerald-600' : summary.matchRate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>{summary.matchRate}%</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">Resolvidas hoje</p>
            <p className="text-2xl font-bold text-emerald-600">{summary.resolvedToday}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Filtros da Fila de Divergências (Issues)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => applyPreset('attention')}>Precisa atenção</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset('orphans')}>Órfãos</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset('missing')}>Sem extrato</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset('resolved_today')}>Resolvidas hoje</Button>
            <span className="text-muted-foreground">|</span>
            <Button size="sm" variant="outline" onClick={() => applyPreset('ops')}>Operação diária</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset('audit')}>Auditoria semanal</Button>
            <Button size="sm" variant="outline" onClick={() => applyPreset('critical')}>Críticos</Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-9 gap-3">
          <div>
            <Label>Status</Label>
            <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="open">Aberta</SelectItem>
                <SelectItem value="in_review">Em análise</SelectItem>
                <SelectItem value="resolved">Resolvida</SelectItem>
                <SelectItem value="waived">Dispensada</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Severidade</Label>
            <Select value={severityFilter} onValueChange={(v: any) => setSeverityFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="critical">Crítica</SelectItem>
                <SelectItem value="high">Alta</SelectItem>
                <SelectItem value="medium">Média</SelectItem>
                <SelectItem value="low">Baixa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Origem</Label>
            <Select value={sourceFilter} onValueChange={(v: any) => setSourceFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="stripe">Sistema • Stripe</SelectItem>
                <SelectItem value="itau">Banco • Itaú</SelectItem>
                <SelectItem value="chase">Banco • Chase</SelectItem>
                <SelectItem value="manual">Lançamento manual</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Método pgto (issues)</Label>
            <Select value={paymentMethodFilter} onValueChange={setPaymentMethodFilter}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {paymentMethodOptions.map((method) => (
                  <SelectItem key={method} value={method}>
                    {PAYMENT_METHOD_LABEL[method] || method}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Responsável</Label>
            <Select value={ownerFilter} onValueChange={(v) => setOwnerFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="unassigned">Sem responsável</SelectItem>
                {assignees.map((assignee) => (
                  <SelectItem key={assignee.id} value={assignee.id}>{assignee.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Tipo</Label>
            <Select value={typeFilter} onValueChange={(v: any) => setTypeFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="missing">Sem extrato</SelectItem>
                <SelectItem value="mismatch">Divergência</SelectItem>
                <SelectItem value="duplicate">Duplicada</SelectItem>
                <SelectItem value="duplicate_suspected">Duplicada suspeita</SelectItem>
                <SelectItem value="orphan">Órfã</SelectItem>
                <SelectItem value="timing">Timing</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>SLA</Label>
            <Select value={slaFilter} onValueChange={(v: any) => setSlaFilter(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="over24">&gt;24h</SelectItem>
                <SelectItem value="over48">&gt;48h</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Abertura (de)</Label>
            <Input type="date" value={issueOpenedFrom} onChange={(e) => setIssueOpenedFrom(e.target.value)} />
          </div>
          <div>
            <Label>Abertura (até)</Label>
            <Input type="date" value={issueOpenedTo} onChange={(e) => setIssueOpenedTo(e.target.value)} />
          </div>
          <div>
            <Label>Busca (issues)</Label>
            <Input
              value={issueSearchTerm}
              onChange={(e) => setIssueSearchTerm(e.target.value)}
              placeholder="Cliente, deal, referência, tipo, fonte..."
            />
          </div>
          <div>
            <Label>Ordenar por</Label>
            <Select value={sortBy} onValueChange={(v: any) => setSortBy(v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sla">SLA (mais antigo)</SelectItem>
                <SelectItem value="severity">Severidade</SelectItem>
                <SelectItem value="amount">Valor</SelectItem>
                <SelectItem value="opened_at">Abertura</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 7);
                setIssueOpenedFrom(start.toISOString().slice(0, 10));
                setIssueOpenedTo(end.toISOString().slice(0, 10));
              }}
            >
              Últimos 7d
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                const end = new Date();
                const start = new Date();
                start.setDate(end.getDate() - 30);
                setIssueOpenedFrom(start.toISOString().slice(0, 10));
                setIssueOpenedTo(end.toISOString().slice(0, 10));
              }}
            >
              Últimos 30d
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setIssueOpenedFrom('');
                setIssueOpenedTo('');
              }}
            >
              Limpar datas
            </Button>
          </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Semáforo SLA (itens abertos)</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">0-24h</p>
            <p className="text-2xl font-bold text-emerald-600">{Math.max(summary.open - summary.over24h, 0)}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">24-48h</p>
            <p className="text-2xl font-bold text-amber-600">{Math.max(summary.over24h - summary.over48h, 0)}</p>
          </div>
          <div className="border rounded-md p-3">
            <p className="text-xs text-muted-foreground">&gt;48h</p>
            <p className="text-2xl font-bold text-red-600">{summary.over48h}</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>SLA por responsável</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {ownerSla.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem itens abertos para SLA por responsável.</p>
          ) : (
            ownerSla.slice(0, 8).map((row) => (
              <div key={row.ownerLabel} className="border rounded-md p-2 text-sm flex items-center justify-between gap-3">
                <p className="font-medium truncate">{row.ownerLabel}</p>
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="outline">Abertas: {row.open}</Badge>
                  <Badge variant={row.over24h > 0 ? 'destructive' : 'outline'}>&gt;24h: {row.over24h}</Badge>
                  <Badge variant={row.over48h > 0 ? 'destructive' : 'outline'}>&gt;48h: {row.over48h}</Badge>
                  <Badge variant={row.critical > 0 ? 'destructive' : 'outline'}>High/Critical: {row.critical}</Badge>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pagamentos para Conciliar</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div>
              <Label>Busca (pagamentos)</Label>
              <Input
                value={paymentSearchTerm}
                onChange={(e) => setPaymentSearchTerm(e.target.value)}
                placeholder="Cliente, deal, parcela..."
              />
            </div>
            <div>
              <Label>Vencimento (de)</Label>
              <Input type="date" value={paymentDueFrom} onChange={(e) => setPaymentDueFrom(e.target.value)} />
            </div>
            <div>
              <Label>Vencimento (até)</Label>
              <Input type="date" value={paymentDueTo} onChange={(e) => setPaymentDueTo(e.target.value)} />
            </div>
            <div className="md:col-span-3 flex items-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const today = new Date().toISOString().slice(0, 10);
                  setPaymentDueFrom(today);
                  setPaymentDueTo(today);
                }}
              >
                Só hoje
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setPaymentDueFrom('');
                  setPaymentDueTo(new Date().toISOString().slice(0, 10));
                }}
              >
                Até hoje
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPaymentSearchTerm('');
                  setPaymentDueFrom('');
                  setPaymentDueTo('');
                }}
              >
                Limpar filtros
              </Button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Fluxo operacional: 1) cobrar e/ou dar baixa em Recebíveis; 2) aqui você concilia. Divergência só existe quando a conciliação não bate. Os filtros de transação aparecem somente ao clicar em "Conciliar com Banco" ou "Conciliar com Transação".
          </p>
          {filteredPaymentReconRows.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum pagamento aberto para os filtros atuais.</p>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Vence hoje</p>
                  <Badge variant="outline">{paymentReconBuckets.dueToday.length}</Badge>
                </div>
                {paymentReconBuckets.dueToday.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum pagamento vencendo hoje.</p>
                ) : (
                  paymentReconBuckets.dueToday.slice(0, 200).map((payment) => (
                    <div key={payment.id} className="border rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{payment.contact_name || '-'}</span></p>
                          <p><span className="text-muted-foreground">Deal:</span> <span className="font-medium">{payment.deal_name || '-'}</span></p>
                          <p><span className="text-muted-foreground">Parcela:</span> <span className="font-medium">{payment.payment_number || payment.description || payment.id.slice(0, 8)}</span></p>
                        </div>
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Vencimento:</span> <span className="font-medium">{payment.due_date ? new Date(payment.due_date).toLocaleDateString() : '-'}</span></p>
                          <p><span className="text-muted-foreground">Saldo:</span> <span className="font-medium">{formatMoney(payment.balance, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">Status:</span> <span className="font-medium">{payment.status}</span></p>
                        </div>
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Total:</span> <span className="font-medium">{formatMoney(payment.amount, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">Pago:</span> <span className="font-medium">{formatMoney(payment.paid_amount, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">ID:</span> <span className="font-medium">{payment.id.slice(0, 8)}</span></p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => openPaymentReconciliation(payment, 'bank_direct')}>
                          Conciliar com Banco
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openPaymentReconciliation(payment, 'payment_tx')}>
                          Conciliar com Transação
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">Atrasadas</p>
                  <Badge variant="destructive">{paymentReconBuckets.overdue.length}</Badge>
                </div>
                {paymentReconBuckets.overdue.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum pagamento atrasado.</p>
                ) : (
                  paymentReconBuckets.overdue.slice(0, 200).map((payment) => (
                    <div key={payment.id} className="border rounded-lg p-3 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{payment.contact_name || '-'}</span></p>
                          <p><span className="text-muted-foreground">Deal:</span> <span className="font-medium">{payment.deal_name || '-'}</span></p>
                          <p><span className="text-muted-foreground">Parcela:</span> <span className="font-medium">{payment.payment_number || payment.description || payment.id.slice(0, 8)}</span></p>
                        </div>
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Vencimento:</span> <span className="font-medium">{payment.due_date ? new Date(payment.due_date).toLocaleDateString() : '-'}</span></p>
                          <p><span className="text-muted-foreground">Saldo:</span> <span className="font-medium">{formatMoney(payment.balance, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">Status:</span> <span className="font-medium">{payment.status}</span></p>
                        </div>
                        <div className="rounded-md border p-2">
                          <p><span className="text-muted-foreground">Total:</span> <span className="font-medium">{formatMoney(payment.amount, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">Pago:</span> <span className="font-medium">{formatMoney(payment.paid_amount, (payment.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                          <p><span className="text-muted-foreground">ID:</span> <span className="font-medium">{payment.id.slice(0, 8)}</span></p>
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Button size="sm" variant="outline" onClick={() => openPaymentReconciliation(payment, 'bank_direct')}>
                          Conciliar com Banco
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openPaymentReconciliation(payment, 'payment_tx')}>
                          Conciliar com Transação
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>
            Fila de Divergências ({sortedIssues.length} exibidas nesta página • total filtrado: {totalIssuesCount})
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={toggleSelectAllVisible}>
              {selectedIssueIds.length > 0 ? 'Selecionar/Desmarcar visíveis' : 'Selecionar visíveis'}
            </Button>
            <Button size="sm" variant="outline" disabled={bulkUpdating || selectedIssueIds.length === 0} onClick={() => bulkUpdateStatus('in_review')}>
              Em análise (lote)
            </Button>
            <Button size="sm" variant="outline" disabled={bulkUpdating || selectedIssueIds.length === 0} onClick={() => bulkUpdateStatus('resolved')}>
              Resolver (lote)
            </Button>
            <Button size="sm" variant="outline" disabled={bulkUpdating || selectedIssueIds.length === 0} onClick={() => bulkUpdateStatus('waived')}>
              Dispensar (lote)
            </Button>
            <span className="text-xs text-muted-foreground">{selectedIssueIds.length} selecionada(s)</span>
          </div>
          {sortedIssues.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">Sem divergências para os filtros selecionados.</div>
          ) : (
            sortedIssues.map((issue) => (
              <div key={issue.id} className="border rounded-lg p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={selectedIssueIds.includes(issue.id)}
                    onChange={() => toggleIssueSelection(issue.id)}
                    aria-label={`Selecionar issue ${issue.id}`}
                  />
                  <span className="text-xs text-muted-foreground">Selecionar</span>
                </div>
                {(() => {
                  const paymentMeta = issue.payment_transaction_id ? paymentMetaByTxId[issue.payment_transaction_id] : null;
                  const bankMeta = issue.bank_transaction_id ? bankMetaById[issue.bank_transaction_id] : null;
                  const matchMeta = matchByIssueId[issue.id];
                  const recordedByName = paymentMeta?.recorded_by ? (ownerNames[paymentMeta.recorded_by] || paymentMeta.recorded_by) : null;
                  const confirmedByName = paymentMeta?.confirmed_by ? (ownerNames[paymentMeta.confirmed_by] || paymentMeta.confirmed_by) : null;
                  const matchedByName = matchMeta?.matched_by ? (ownerNames[matchMeta.matched_by] || matchMeta.matched_by) : null;
                  return (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div className="rounded-md border p-2">
                        <p><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{paymentMeta?.contact_name || '-'}</span></p>
                        <p><span className="text-muted-foreground">Deal:</span> <span className="font-medium">{paymentMeta?.deal_name || '-'}</span></p>
                        <p><span className="text-muted-foreground">Método:</span> <span className="font-medium">{paymentMeta?.payment_method || '-'}</span></p>
                        <p><span className="text-muted-foreground">Vencimento:</span> <span className="font-medium">{paymentMeta?.due_date ? new Date(paymentMeta.due_date).toLocaleDateString() : '-'}</span></p>
                        <p><span className="text-muted-foreground">Pagamento em:</span> <span className="font-medium">{paymentMeta?.transaction_date ? new Date(paymentMeta.transaction_date).toLocaleDateString() : '-'}</span></p>
                        <p><span className="text-muted-foreground">Ref pgto:</span> <span className="font-medium">{paymentMeta?.reference_number || '-'}</span></p>
                      </div>
                      <div className="rounded-md border p-2">
                        <p>
                          <span className="text-muted-foreground">Esperado:</span>{' '}
                          <span className="font-medium">
                            {typeof issue.amount_expected === 'number' ? formatMoney(issue.amount_expected, (issue.currency as 'USD' | 'BRL') || 'USD') : '-'}
                          </span>
                        </p>
                        <p>
                          <span className="text-muted-foreground">Encontrado:</span>{' '}
                          <span className="font-medium">
                            {typeof issue.amount_found === 'number' ? formatMoney(issue.amount_found, (issue.currency as 'USD' | 'BRL') || 'USD') : '-'}
                          </span>
                        </p>
                        <p><span className="text-muted-foreground">Fonte issue:</span> <span className="font-medium">{SOURCE_LABEL[issue.source] || issue.source}</span></p>
                        <p><span className="text-muted-foreground">Fonte conciliada:</span> <span className="font-medium">{matchMeta?.source ? (SOURCE_LABEL[matchMeta.source] || matchMeta.source) : '-'}</span></p>
                      </div>
                      <div className="rounded-md border p-2">
                        <p><span className="text-muted-foreground">Lançado por:</span> <span className="font-medium">{recordedByName || '-'}</span></p>
                        <p><span className="text-muted-foreground">Confirmado por:</span> <span className="font-medium">{confirmedByName || '-'}</span></p>
                        <p><span className="text-muted-foreground">Conciliado por:</span> <span className="font-medium">{matchedByName || '-'}</span></p>
                        <p><span className="text-muted-foreground">Compensado no banco:</span> <span className="font-medium">{bankMeta?.booked_at ? new Date(bankMeta.booked_at).toLocaleDateString() : '-'}</span></p>
                        <p><span className="text-muted-foreground">Data conciliação:</span> <span className="font-medium">{matchMeta?.matched_at ? new Date(matchMeta.matched_at).toLocaleString() : '-'}</span></p>
                      </div>
                      <div className="rounded-md border p-2 md:col-span-3">
                        <p><span className="text-muted-foreground">Ref banco:</span> <span className="font-medium">{bankMeta?.reference || '-'}</span></p>
                        <p className="truncate"><span className="text-muted-foreground">Desc banco:</span> <span className="font-medium">{bankMeta?.description || '-'}</span></p>
                        <p className="truncate"><span className="text-muted-foreground">Nota conciliação:</span> <span className="font-medium">{matchMeta?.notes || '-'}</span></p>
                      </div>
                    </div>
                  );
                })()}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={severityClass(issue.severity)}>{SEVERITY_LABEL[issue.severity]}</Badge>
                  <Badge variant="outline">{STATUS_LABEL[issue.status]}</Badge>
                  <Badge variant="outline">{ISSUE_TYPE_LABEL[issue.issue_type] || issue.issue_type}</Badge>
                  {getIssueAgeHours(issue.opened_at) >= 48 ? <Badge variant="destructive">SLA &gt;48h</Badge> : null}
                  {getIssueAgeHours(issue.opened_at) >= 24 && getIssueAgeHours(issue.opened_at) < 48 ? <Badge className="bg-amber-100 text-amber-700 border-amber-300">SLA &gt;24h</Badge> : null}
                  <span className="text-xs text-muted-foreground">{new Date(issue.opened_at).toLocaleString()}</span>
                </div>
                <div className="text-sm">
                  IDs: issue <span className="font-medium">{issue.id.slice(0, 8)}</span>
                  {issue.payment_transaction_id ? <> • pgto <span className="font-medium">{issue.payment_transaction_id.slice(0, 8)}</span></> : null}
                  {issue.bank_transaction_id ? <> • banco <span className="font-medium">{issue.bank_transaction_id.slice(0, 8)}</span></> : null}
                </div>
                <div className="text-xs text-muted-foreground">
                  Responsável: {issue.owner_user_id ? ownerNames[issue.owner_user_id] || 'Sem nome' : 'Sem responsável'}
                </div>
                <div className="max-w-xs">
                  <Label className="text-xs">Atribuir responsável</Label>
                  <Select
                    value={issue.owner_user_id || 'unassigned'}
                    onValueChange={async (value) => {
                      try {
                        await assignIssueOwner(issue.id, value === 'unassigned' ? null : value);
                        await loadData();
                      } catch {
                        toast.error('Falha ao atribuir responsável');
                      }
                    }}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Sem responsável" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Sem responsável</SelectItem>
                      {assignees.map((assignee) => (
                        <SelectItem key={assignee.id} value={assignee.id}>{assignee.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {typeof issue.amount_expected === 'number' && typeof issue.amount_found === 'number' ? (
                  <div className="text-xs text-muted-foreground">
                    Diferença: {formatMoney(issue.amount_found - issue.amount_expected, (issue.currency as 'USD' | 'BRL') || 'USD')}
                  </div>
                ) : null}
                {auditByIssue[issue.id]?.length ? (
                  <div className="rounded-md border bg-muted/30 p-2 space-y-1">
                    <p className="text-xs font-medium">Histórico de alterações</p>
                    {auditByIssue[issue.id].slice(0, 4).map((row) => (
                      <div key={row.id} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{AUDIT_FIELD_LABEL[row.field_name] || row.field_name}</span>
                        {' '}de{' '}
                        <span className="text-foreground">{row.old_value ?? '-'}</span>
                        {' '}para{' '}
                        <span className="text-foreground">{row.new_value ?? '-'}</span>
                        {' '}por{' '}
                        <span className="text-foreground">{row.changed_by_app_user_id ? (ownerNames[row.changed_by_app_user_id] || row.changed_by_app_user_id) : 'Sistema'}</span>
                        {' '}em {new Date(row.changed_at).toLocaleString()}
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" onClick={() => openIssueSheet(issue)}>
                    <Eye className="h-3.5 w-3.5 mr-1" />
                    Detalhes
                  </Button>

                  {issue.status === 'open' || issue.status === 'in_review' ? (
                    <>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await updateIssueStatus(issue.id, 'in_review');
                            await loadData();
                          } catch {
                            toast.error('Falha ao mover para in_review');
                          }
                        }}
                      >
                        <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                        Marcar In Review
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await updateIssueStatus(issue.id, 'resolved', 'Resolvido manualmente sem vínculo bancário');
                            await loadData();
                          } catch {
                            toast.error('Falha ao resolver issue');
                          }
                        }}
                      >
                        Resolver
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await updateIssueStatus(issue.id, 'waived', 'Dispensado por decisão operacional');
                            await loadData();
                          } catch {
                            toast.error('Falha ao dispensar issue');
                          }
                        }}
                      >
                        Dispensar
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={async () => {
                        try {
                          await updateIssueStatus(issue.id, 'open');
                          await loadData();
                        } catch {
                          toast.error('Falha ao reabrir issue');
                        }
                      }}
                    >
                      Reabrir
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
          <div className="pt-2 flex items-center justify-between">
            <p className="text-xs text-muted-foreground">
              Página {currentPage} de {totalPages} • {PAGE_SIZE} por página
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage <= 1 || loading}
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              >
                Anterior
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={currentPage >= totalPages || loading}
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              >
                Próxima
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      <Dialog
        open={paymentReconSheetOpen}
        onOpenChange={(open) => {
          if (!open) {
            setPaymentReconSheetOpen(false);
            setActivePaymentRecon(null);
            setSelectedReconBankTxId('');
            setSelectedReconPaymentTxId('');
            setPaymentReconReasonCode('partial_stripe');
            setPaymentReconReasonDetail('');
            setPaymentReconAllocationAmount('');
            setConfirmCrossClientAllocation(false);
          }
        }}
      >
        <DialogContent className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Conciliar Pagamento</DialogTitle>
          </DialogHeader>
          {activePaymentRecon ? (
            <div className="space-y-4 mt-4">
              <div className="rounded-md border p-3 text-xs space-y-1">
                <p><span className="text-muted-foreground">Cliente:</span> {activePaymentRecon.contact_name || '-'}</p>
                <p><span className="text-muted-foreground">Deal:</span> {activePaymentRecon.deal_name || '-'}</p>
                <p><span className="text-muted-foreground">Parcela:</span> {activePaymentRecon.payment_number || activePaymentRecon.description || activePaymentRecon.id.slice(0, 8)}</p>
                <p><span className="text-muted-foreground">Vencimento:</span> {activePaymentRecon.due_date ? new Date(activePaymentRecon.due_date).toLocaleDateString() : '-'}</p>
                <p><span className="text-muted-foreground">Saldo aberto:</span> {formatMoney(activePaymentRecon.balance, (activePaymentRecon.currency as 'USD' | 'BRL') || 'USD')}</p>
              </div>

              <div>
                <Label className="text-xs">Modo de conciliação</Label>
                <Select
                  value={paymentReconMode}
                  onValueChange={(v: any) => {
                    setPaymentReconMode(v);
                    setSelectedReconBankTxId('');
                    setSelectedReconPaymentTxId('');
                    setPaymentReconReasonCode(v === 'payment_tx' ? 'partial_stripe' : 'manual_override');
                    setPaymentReconReasonDetail('');
                    setPaymentReconAllocationAmount('');
                    setConfirmCrossClientAllocation(false);
                  }}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_direct">Payment -&gt; BankTransaction (PIX/Zelle/transferência)</SelectItem>
                    <SelectItem value="payment_tx">Payment -&gt; PaymentTransaction (Stripe)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {paymentReconMode === 'bank_direct' ? (
                <div className="space-y-2">
                  <Label className="text-xs">Transação bancária alvo ({bankCandidatesForActivePayment.length})</Label>
                  <div className="max-h-56 overflow-y-auto border rounded-md">
                    {bankCandidatesForActivePayment.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">Nenhuma transação bancária compatível encontrada.</p>
                    ) : (
                      bankCandidatesForActivePayment.map((bank) => (
                        <button
                          key={bank.id}
                          type="button"
                          className={`w-full text-left p-2 text-xs border-b last:border-b-0 hover:bg-muted/50 ${selectedReconBankTxId === bank.id ? 'bg-primary/10 font-medium' : ''}`}
                          onClick={() => setSelectedReconBankTxId(bank.id)}
                        >
                          <p>{new Date(bank.booked_at).toLocaleDateString()} • {formatMoney(Math.abs(Number(bank.amount || 0)), (bank.currency as 'USD' | 'BRL') || 'USD')} • {SOURCE_LABEL[bank.source] || bank.source}</p>
                          <p className="text-muted-foreground truncate">{bank.reference || bank.description || 'sem ref.'}</p>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs">Transação de pagamento alvo ({paymentTxCandidates.length})</Label>
                  <div className="max-h-[50vh] overflow-y-auto border rounded-md p-2 space-y-2">
                    {paymentTxCandidates.length === 0 ? (
                      <p className="p-2 text-xs text-muted-foreground">Nenhuma transação de pagamento compatível encontrada.</p>
                    ) : (
                      paymentTxCandidates.map((tx) => (
                        <button
                          key={tx.id}
                          type="button"
                          className={`w-full text-left p-3 text-xs border rounded-md hover:bg-muted/40 ${
                            selectedReconPaymentTxId === tx.id ? 'border-primary bg-primary/10' : 'border-border'
                          }`}
                          onClick={() => {
                            setSelectedReconPaymentTxId(tx.id);
                            const maxAuto = Math.max(
                              Math.min(
                                Number(tx.remaining_allocatable || 0),
                                Number(activePaymentRecon?.balance || 0)
                              ),
                              0
                            );
                            setPaymentReconAllocationAmount(maxAuto > 0 ? maxAuto.toFixed(2) : '');
                            setConfirmCrossClientAllocation(false);
                          }}
                        >
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div className="space-y-1">
                              <p><span className="text-muted-foreground">Cliente:</span> <span className="font-medium">{tx.contact_name || '-'}</span></p>
                              <p><span className="text-muted-foreground">Deal:</span> <span className="font-medium">{tx.deal_name || '-'}</span></p>
                              <p><span className="text-muted-foreground">Parcela:</span> <span className="font-medium">{tx.payment_number || tx.payment_description || tx.payment_id?.slice(0, 8) || '-'}</span></p>
                              <p><span className="text-muted-foreground">Ref pgto:</span> <span className="font-medium">{tx.reference_number || '-'}</span></p>
                            </div>
                            <div className="space-y-1">
                              <p><span className="text-muted-foreground">Data:</span> <span className="font-medium">{tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString() : '-'}</span></p>
                              <p><span className="text-muted-foreground">Valor:</span> <span className="font-medium">{typeof tx.amount === 'number' ? formatMoney(tx.amount, (tx.currency as 'USD' | 'BRL') || 'USD') : '-'}</span></p>
                              <p><span className="text-muted-foreground">Método:</span> <span className="font-medium">{PAYMENT_METHOD_LABEL[tx.payment_method || ''] || tx.payment_method || '-'}</span></p>
                              <p><span className="text-muted-foreground">Status tx:</span> <span className="font-medium">{tx.transaction_status || '-'}</span></p>
                              <p><span className="text-muted-foreground">Status recebível:</span> <span className="font-medium">{tx.payment_status || '-'}</span></p>
                              {tx.is_legacy_linked && (
                                <p>
                                  <span className="text-muted-foreground">Origem:</span>{' '}
                                  <span className="font-medium text-amber-700">legado vinculado (sem allocations)</span>
                                </p>
                              )}
                              <p><span className="text-muted-foreground">Saldo restante da transação:</span> <span className="font-medium">{formatMoney(Number(tx.remaining_allocatable || 0), (tx.currency as 'USD' | 'BRL') || 'USD')}</span></p>
                              <p><span className="text-muted-foreground">ID:</span> <span className="font-medium">{tx.id.slice(0, 8)}</span></p>
                            </div>
                          </div>
                          <div className="mt-2 flex justify-end">
                            <span className="text-[11px] px-2 py-1 rounded border">
                              {selectedReconPaymentTxId === tx.id ? 'Selecionado' : 'Selecionar'}
                            </span>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}

              {paymentReconMode === 'payment_tx' && (
                <div className="space-y-2">
                  <Label className="text-xs">Valor a alocar nesta parcela</Label>
                  <Input
                    inputMode="decimal"
                    value={paymentReconAllocationAmount}
                    onChange={(e) => setPaymentReconAllocationAmount(e.target.value)}
                    placeholder="Ex.: 250.00"
                  />
                  <p className="text-xs text-muted-foreground">
                    Máximo permitido nesta seleção: {formatMoney(maxAllocatableForSelection, (activePaymentRecon?.currency as 'USD' | 'BRL') || 'USD')}
                  </p>
                </div>
              )}

              <div>
                <Label className="text-xs">Motivo (obrigatório)</Label>
                <div className="space-y-2">
                  <Select value={paymentReconReasonCode} onValueChange={(value: PaymentReconReasonCode) => setPaymentReconReasonCode(value)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o motivo" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_RECON_REASON_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    value={paymentReconReasonDetail}
                    onChange={(e) => setPaymentReconReasonDetail(e.target.value)}
                    placeholder="Detalhe opcional para auditoria"
                  />
                </div>
              </div>

              {paymentReconMode === 'payment_tx' && isCrossClientSelection && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
                  <p className="text-xs text-amber-900">
                    Alocação cross-client detectada: a transação selecionada pertence a outro cliente/deal.
                  </p>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="confirm-cross-client-allocation"
                      checked={confirmCrossClientAllocation}
                      onCheckedChange={(checked) => setConfirmCrossClientAllocation(Boolean(checked))}
                    />
                    <Label htmlFor="confirm-cross-client-allocation" className="text-xs">
                      Confirmo esta alocação cross-client e assumo a justificativa de auditoria.
                    </Label>
                  </div>
                </div>
              )}

              <Button
                size="sm"
                onClick={savePaymentReconciliation}
                disabled={
                  savingPaymentRecon ||
                  !paymentReconReasonCode ||
                  (paymentReconReasonCode === 'other' && !paymentReconReasonDetail.trim()) ||
                  (paymentReconMode === 'payment_tx' &&
                    Boolean(selectedReconPaymentTx?.is_legacy_linked) &&
                    !['manual_override', 'cross_client_allocation'].includes(paymentReconReasonCode)) ||
                  (paymentReconMode === 'payment_tx' &&
                    Boolean(selectedReconPaymentTx?.is_legacy_linked) &&
                    !paymentReconReasonDetail.trim()) ||
                  (paymentReconMode === 'payment_tx' && !paymentReconAllocationAmount.trim()) ||
                  (paymentReconMode === 'payment_tx' && Number(String(paymentReconAllocationAmount).replace(',', '.')) <= 0) ||
                  (paymentReconMode === 'payment_tx' && Number(String(paymentReconAllocationAmount).replace(',', '.')) > maxAllocatableForSelection) ||
                  (paymentReconMode === 'payment_tx' && isCrossClientSelection && !confirmCrossClientAllocation) ||
                  (paymentReconMode === 'bank_direct' ? !selectedReconBankTxId : !selectedReconPaymentTxId)
                }
              >
                {savingPaymentRecon ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LinkIcon className="h-4 w-4 mr-2" />}
                Conciliar Pagamento
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={sheetOpen} onOpenChange={(open) => { if (!open) { setSheetOpen(false); setActiveIssueId(null); setSelectedPaymentId(''); setSelectedPaymentTransactionId(''); } }}>
        <DialogContent className="w-full max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalhes da Issue</DialogTitle>
          </DialogHeader>
          {selectedIssue ? (() => {
            const paymentMeta = selectedIssue.payment_transaction_id ? paymentMetaByTxId[selectedIssue.payment_transaction_id] : null;
            const bankMeta = selectedIssue.bank_transaction_id ? bankMetaById[selectedIssue.bank_transaction_id] : null;
            const matchMeta = matchByIssueId[selectedIssue.id];
            const auditRows = auditByIssue[selectedIssue.id] || [];
            return (
              <div className="space-y-4 mt-4">
                {/* Issue info */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className={severityClass(selectedIssue.severity)}>{SEVERITY_LABEL[selectedIssue.severity]}</Badge>
                  <Badge variant="outline">{STATUS_LABEL[selectedIssue.status]}</Badge>
                  <Badge variant="outline">{ISSUE_TYPE_LABEL[selectedIssue.issue_type] || selectedIssue.issue_type}</Badge>
                  {getIssueAgeHours(selectedIssue.opened_at) >= 48 && <Badge variant="destructive">SLA &gt;48h</Badge>}
                  {getIssueAgeHours(selectedIssue.opened_at) >= 24 && getIssueAgeHours(selectedIssue.opened_at) < 48 && <Badge className="bg-amber-100 text-amber-700 border-amber-300">SLA &gt;24h</Badge>}
                </div>
                <p className="text-xs text-muted-foreground">Aberta em {new Date(selectedIssue.opened_at).toLocaleString()}</p>

                {/* Payment context */}
                {paymentMeta && (
                  <div className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">Transação de Pagamento</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <p><span className="text-muted-foreground">Cliente:</span> {paymentMeta.contact_name || '-'}</p>
                      <p><span className="text-muted-foreground">Deal:</span> {paymentMeta.deal_name || '-'}</p>
                      <p><span className="text-muted-foreground">Valor:</span> {typeof paymentMeta.amount === 'number' ? formatMoney(paymentMeta.amount, (paymentMeta.currency as 'USD' | 'BRL') || 'USD') : '-'}</p>
                      <p><span className="text-muted-foreground">Pagamento em:</span> {paymentMeta.transaction_date ? new Date(paymentMeta.transaction_date).toLocaleDateString() : '-'}</p>
                      <p><span className="text-muted-foreground">Vencimento:</span> {paymentMeta.due_date ? new Date(paymentMeta.due_date).toLocaleDateString() : '-'}</p>
                      <p><span className="text-muted-foreground">Status recebível:</span> {paymentMeta.payment_status || '-'}</p>
                      <p><span className="text-muted-foreground">Método:</span> {PAYMENT_METHOD_LABEL[paymentMeta.payment_method || ''] || paymentMeta.payment_method || '-'}</p>
                      <p><span className="text-muted-foreground">Ref:</span> {paymentMeta.reference_number || '-'}</p>
                      <p><span className="text-muted-foreground">Confirmado por:</span> {paymentMeta.confirmed_by ? (ownerNames[paymentMeta.confirmed_by] || paymentMeta.confirmed_by) : '-'}</p>
                      <p><span className="text-muted-foreground">Em:</span> {paymentMeta.confirmed_at ? new Date(paymentMeta.confirmed_at).toLocaleString() : '-'}</p>
                    </div>
                  </div>
                )}

                {/* Bank context */}
                {bankMeta && (
                  <div className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">Transação Bancária</p>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <p><span className="text-muted-foreground">Valor:</span> {formatMoney(Number(bankMeta.amount), (bankMeta.currency as 'USD' | 'BRL') || 'USD')}</p>
                      <p><span className="text-muted-foreground">Compensado em:</span> {new Date(bankMeta.booked_at).toLocaleDateString()}</p>
                      <p><span className="text-muted-foreground">Direção:</span> {bankMeta.direction === 'in' ? 'Entrada' : 'Saída'}</p>
                      <p><span className="text-muted-foreground">Fonte:</span> {SOURCE_LABEL[bankMeta.source] || bankMeta.source}</p>
                      <p className="col-span-2"><span className="text-muted-foreground">Referência:</span> {bankMeta.reference || '-'}</p>
                      <p className="col-span-2 truncate"><span className="text-muted-foreground">Descrição:</span> {bankMeta.description || '-'}</p>
                    </div>
                  </div>
                )}

                {/* Match info */}
                {matchMeta && (
                  <div className="rounded-md border p-3 space-y-1">
                    <p className="text-sm font-medium">Match Existente</p>
                    <div className="text-xs space-y-1">
                      <p><span className="text-muted-foreground">Conciliado por:</span> {matchMeta.matched_by ? (ownerNames[matchMeta.matched_by] || matchMeta.matched_by) : 'Sistema'}</p>
                      <p><span className="text-muted-foreground">Data:</span> {new Date(matchMeta.matched_at).toLocaleString()}</p>
                      <p><span className="text-muted-foreground">Nota:</span> {matchMeta.notes || '-'}</p>
                    </div>
                  </div>
                )}

                {/* Suggested candidates for missing issues */}
                {selectedIssue.issue_type === 'missing' && candidateBankTxns.length > 0 && (
                  <div className="rounded-md border p-3 space-y-2">
                    <p className="text-sm font-medium">Candidatos Sugeridos ({candidateBankTxns.length})</p>
                    <p className="text-xs text-muted-foreground">Transações bancárias com valor similar (±5%) e data próxima (±7 dias)</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {candidateBankTxns.slice(0, 10).map((btx) => (
                        <div key={btx.id} className="flex items-center justify-between border rounded p-2 text-xs hover:bg-muted/50">
                          <div className="space-y-0.5">
                            <p>{new Date(btx.booked_at).toLocaleDateString()} • {formatMoney(Number(btx.amount), (btx.currency as 'USD' | 'BRL') || 'USD')} • {btx.direction === 'in' ? 'entrada' : 'saída'}</p>
                            <p className="text-muted-foreground truncate max-w-[280px]">{btx.reference || btx.description || 'sem ref.'}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs shrink-0"
                            onClick={() => {
                              setSelectedBankTxnId(btx.id);
                            }}
                          >
                            <LinkIcon className="h-3 w-3 mr-1" />
                            {selectedBankTxnId === btx.id ? 'Selecionado' : 'Vincular'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Suggested candidates for orphan issues */}
                {selectedIssue.issue_type === 'orphan' && candidatePaymentTxns.length > 0 && (
                  <div className="rounded-md border p-3 space-y-2">
                    <p className="text-sm font-medium">Transações de Pagamento Similares ({candidatePaymentTxns.length})</p>
                    <p className="text-xs text-muted-foreground">Transações de pagamento com valor similar (±5%) e data próxima (±7 dias)</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {candidatePaymentTxns.slice(0, 10).map((pmeta) => (
                        <div key={pmeta.id} className="flex items-center justify-between gap-2 border rounded p-2 text-xs hover:bg-muted/50">
                          <div className="min-w-0">
                            <p>{pmeta.transaction_date ? new Date(pmeta.transaction_date).toLocaleDateString() : '-'} • {typeof pmeta.amount === 'number' ? formatMoney(pmeta.amount, (pmeta.currency as 'USD' | 'BRL') || 'USD') : '-'}</p>
                            <p className="text-muted-foreground truncate">{pmeta.contact_name || '-'} • {pmeta.deal_name || '-'}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs shrink-0"
                            onClick={() => {
                              setSelectedPaymentTransactionId(pmeta.id);
                              setSelectedPaymentId('');
                            }}
                          >
                            {selectedPaymentTransactionId === pmeta.id ? 'Selecionado' : 'Usar'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedIssue.issue_type === 'orphan' && candidateReceivables.length > 0 && (
                  <div className="rounded-md border p-3 space-y-2">
                    <p className="text-sm font-medium">Recebíveis em Aberto ({candidateReceivables.length})</p>
                    <p className="text-xs text-muted-foreground">Para fluxo D+1: conciliar direto no recebível. A transação é criada automaticamente.</p>
                    <div className="space-y-1 max-h-56 overflow-y-auto">
                      {candidateReceivables.slice(0, 20).map((receivable) => (
                        <div key={receivable.id} className="flex items-center justify-between gap-2 border rounded p-2 text-xs hover:bg-muted/50">
                          <div className="min-w-0">
                            <p>
                              Venc. {receivable.due_date ? new Date(receivable.due_date).toLocaleDateString() : '-'} •
                              Saldo {formatMoney(Number(receivable.balance || 0), (receivable.currency as 'USD' | 'BRL') || 'USD')}
                            </p>
                            <p className="text-muted-foreground truncate">
                              {receivable.contact_name || '-'} • {receivable.deal_name || '-'} • {receivable.payment_number || receivable.description || receivable.id.slice(0, 8)}
                            </p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs shrink-0"
                            onClick={() => {
                              setSelectedPaymentId(receivable.id);
                              setSelectedPaymentTransactionId('');
                            }}
                          >
                            {selectedPaymentId === receivable.id ? 'Selecionado' : 'Vincular recebível'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selectedIssue.issue_type === 'missing' && candidateBankTxns.length === 0 && (
                  <p className="text-xs text-muted-foreground">Nenhuma transação bancária com valor/data próximos encontrada.</p>
                )}

                {/* Manual match form */}
                {(selectedIssue.status === 'open' || selectedIssue.status === 'in_review') && (
                  <div className="rounded-md border p-3 space-y-3">
                    <p className="text-sm font-medium">Match Manual</p>
                    <div>
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">
                          Transação bancária ({manualBankScope === 'compatible' ? `${eligibleBankTxOptions.length} compatíveis` : `${bankTxOptions.length} total`})
                        </Label>
                        <div className="flex items-center gap-1">
                          <Button
                            type="button"
                            size="sm"
                            variant={manualBankScope === 'compatible' ? 'default' : 'outline'}
                            className="h-7 px-2 text-[11px]"
                            onClick={() => setManualBankScope('compatible')}
                          >
                            Compatíveis
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant={manualBankScope === 'all' ? 'default' : 'outline'}
                            className="h-7 px-2 text-[11px]"
                            onClick={() => setManualBankScope('all')}
                          >
                            Todas
                          </Button>
                        </div>
                      </div>
                      <div className="max-h-52 overflow-y-auto border rounded-md mt-1">
                        {manualBankOptions.length === 0 ? (
                          <div className="p-2 space-y-2">
                            <p className="text-xs text-muted-foreground">Nenhuma transação bancária compatível (direção/moeda).</p>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => setManualBankScope('all')}
                            >
                              Mostrar todas para override
                            </Button>
                          </div>
                        ) : manualBankOptions.map((btx) => (
                          <button
                            key={btx.id}
                            type="button"
                            className={`w-full text-left p-2 text-xs border-b last:border-b-0 hover:bg-muted/50 ${selectedBankTxnId === btx.id ? 'bg-primary/10 font-medium' : ''}`}
                            onClick={() => setSelectedBankTxnId(btx.id)}
                          >
                            <p>{new Date(btx.booked_at).toLocaleDateString()} • {formatMoney(Number(btx.amount || 0), (btx.currency as 'USD' | 'BRL') || 'USD')} • {btx.direction === 'in' ? 'entrada' : 'saída'}</p>
                            <p className="text-muted-foreground truncate">{btx.reference || btx.description || 'sem ref.'}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs">Motivo (obrigatório)</Label>
                      <Textarea value={manualNote} onChange={(e) => setManualNote(e.target.value)} placeholder="Explique o racional do match manual" rows={2} />
                    </div>
                    {selectedIssue.issue_type === 'orphan' && (
                      <p className="text-xs text-muted-foreground">
                        Seleção atual: {selectedPaymentId ? `recebível ${selectedPaymentId.slice(0, 8)}` : (selectedPaymentTransactionId ? `transação ${selectedPaymentTransactionId.slice(0, 8)}` : 'nenhum recebível/transação selecionado')}
                      </p>
                    )}
                    <Button
                      size="sm"
                      onClick={saveManualMatch}
                      disabled={
                        savingManualMatch ||
                        !selectedBankTxnId ||
                        !manualNote.trim() ||
                        (selectedIssue.issue_type === 'orphan' && !selectedPaymentId && !selectedPaymentTransactionId && !selectedIssue.payment_transaction_id)
                      }
                    >
                      {savingManualMatch ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <LinkIcon className="h-4 w-4 mr-2" />}
                      Salvar Match
                    </Button>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap items-center gap-2 border-t pt-3">
                  {(selectedIssue.status === 'open' || selectedIssue.status === 'in_review') ? (
                    <>
                      <Button size="sm" variant="outline" onClick={async () => { try { await updateIssueStatus(selectedIssue.id, 'in_review'); await loadData(); } catch { toast.error('Falha ao mover para in_review'); } }}>
                        <ShieldAlert className="h-3.5 w-3.5 mr-1" />
                        In Review
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => { try { await updateIssueStatus(selectedIssue.id, 'resolved', 'Resolvido manualmente sem vínculo bancário'); setSheetOpen(false); await loadData(); } catch { toast.error('Falha ao resolver'); } }}>
                        Resolver
                      </Button>
                      <Button size="sm" variant="outline" onClick={async () => { try { await updateIssueStatus(selectedIssue.id, 'waived', 'Dispensado por decisão operacional'); setSheetOpen(false); await loadData(); } catch { toast.error('Falha ao dispensar'); } }}>
                        Dispensar
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" onClick={async () => { try { await updateIssueStatus(selectedIssue.id, 'open'); await loadData(); } catch { toast.error('Falha ao reabrir'); } }}>
                      Reabrir
                    </Button>
                  )}
                  <div className="ml-auto max-w-[180px]">
                    <Select
                      value={selectedIssue.owner_user_id || 'unassigned'}
                      onValueChange={async (value) => { try { await assignIssueOwner(selectedIssue.id, value === 'unassigned' ? null : value); await loadData(); } catch { toast.error('Falha ao atribuir'); } }}
                    >
                      <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Atribuir" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Sem responsável</SelectItem>
                        {assignees.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Audit history */}
                {auditRows.length > 0 && (
                  <div className="rounded-md border bg-muted/30 p-3 space-y-1">
                    <p className="text-sm font-medium">Histórico</p>
                    {auditRows.slice(0, 8).map((row) => (
                      <div key={row.id} className="text-xs text-muted-foreground">
                        <span className="font-medium text-foreground">{AUDIT_FIELD_LABEL[row.field_name] || row.field_name}</span>
                        {' '}de <span className="text-foreground">{row.old_value ?? '-'}</span>
                        {' '}para <span className="text-foreground">{row.new_value ?? '-'}</span>
                        {' '}por <span className="text-foreground">{row.changed_by_app_user_id ? (ownerNames[row.changed_by_app_user_id] || row.changed_by_app_user_id) : 'Sistema'}</span>
                        {' '}em {new Date(row.changed_at).toLocaleString()}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })() : <p className="text-sm text-muted-foreground mt-4">Nenhuma issue selecionada.</p>}
        </DialogContent>
      </Dialog>
    </FinancePageLayout>
  );
}
